// Sales Forecasting Data Aggregation Utilities
// This module handles data aggregation across different hierarchy levels

import { 
  SalesHierarchyNode, 
  HierarchyLevel, 
  TimePeriod, 
  SalesDataPoint,
  SalesMetrics,
  AggregationResult,
  HierarchyPath,
  TableRow,
  type ColumnConfig,
} from '../types'
import {
  build_monthly_data_from_points,
  calculate_totals_from_points,
} from "./monthly_metrics_view"
import { FORECAST_DETAIL, resolve_display_metrics, select_primary_sales_rows } from "./forecast_measure_slice"
import { cases_from_units, metrics_part_cases, node_units_per_case, point_quantity_basis, units_from_cases } from "./quantity_basis"
import { parseISO, isAfter, format } from 'date-fns'
import { round_cases } from "./quantity_basis"

/** Sum per-key `metrics.detail` values when rolling up hierarchy nodes (retailer CSV slices). */
function merge_detail_metrics_sum(
  a: Record<string, number> | undefined,
  b: Record<string, number> | undefined,
): Record<string, number> | undefined {
  if (!a && !b) {
    return undefined
  }
  const keys = new Set<string>([...Object.keys(a || {}), ...Object.keys(b || {})])
  const out: Record<string, number> = {}
  for (const k of keys) {
    const va = a && typeof a[k] === "number" && Number.isFinite(a[k]) ? a[k] : 0
    const vb = b && typeof b[k] === "number" && Number.isFinite(b[k]) ? b[k] : 0
    const s = va + vb
    if (s !== 0 || (a && k in a) || (b && k in b)) {
      out[k] = s
    }
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/**
 * Add two optional unit counts, keeping `undefined` when *neither* side reports units.
 *
 * Summing over no units is unknown, not zero: a source that carries dollars but no unit volume
 * for a period (Confido's 2026 actual months, say) must roll up to "—" on the parent row rather
 * than a 0 that reads as "sold nothing". Once any contributor reports units, silent siblings
 * count as zero.
 */
function sum_optional_units(a: number | undefined | null, b: number | undefined | null): number | undefined {
  if (a == null && b == null) {
    return undefined
  }
  return Number(a ?? 0) + Number(b ?? 0)
}

/**
 * Doors do not add up the tree — they overlap.
 *
 * A customer's four SKUs sit in the *same* stores, so summing their store counts multiplies one
 * door set by the number of items in it: an 850-store Kroger listing across 4 SKUs rolled up to
 * 3,400. Volume adds; distribution does not. Every roll-up therefore takes the largest child
 * value as the parent's door count — the widest distribution any child reaches — which is exact
 * for the common case (co-located items) and conservative for the rest.
 *
 * The trade-off is deliberate: two genuinely disjoint store sets under one parent report the
 * larger rather than the total. Understating doors is far safer than an inflation that scales
 * with SKU count, and there is no per-row store list to reconcile the two against.
 */
export function max_store_count(a: number | undefined | null, b: number | undefined | null): number {
  return Math.max(Number(a ?? 0), Number(b ?? 0))
}

function add_sales_metrics_into_target(target: SalesMetrics, add: SalesMetrics): void {
  target.store_count = max_store_count(target.store_count, add.store_count)
  // Distribution points DO sum (unlike doors): a child that is itself an aggregate carries
  // its own sum; a leaf contributes its store count.
  target.sku_store_count = (target.sku_store_count ?? 0) + Number(add.sku_store_count ?? add.store_count ?? 0)
  target.volume_dollars += add.volume_dollars
  // Collect the children's part-cases first: once `cases_sold` and dollars are summed, the
  // target's own numbers no longer say which children held less than a whole case.
  target.part_cases = Number(target.part_cases ?? 0) + metrics_part_cases(add)
  target.cases_sold += add.cases_sold
  target.units_sold = sum_optional_units(target.units_sold, add.units_sold)
  const d = merge_detail_metrics_sum(target.detail, add.detail)
  if (d) {
    target.detail = d
  }
}

/**
 * Aggregates sales data from child nodes up to parent nodes
 * This is crucial for handling varying granularity levels
 */
export function aggregateSalesData(
  nodes: SalesHierarchyNode[],
  targetLevel: HierarchyLevel,
  periods: TimePeriod[]
): SalesDataPoint[] {
  const aggregatedData: { [periodKey: string]: SalesDataPoint } = {}

  // Initialize all periods
  periods.forEach(period => {
    aggregatedData[period.period_key] = {
      period,
        metrics: {
          store_count: 0,
          volume_dollars: 0,
          cases_sold: 0,
          case_cost: 0
        }
    }
  })

  // Aggregate data from all nodes at the target level and below
  nodes.forEach(node => {
    if (shouldIncludeNode(node, targetLevel)) {
      node.sales_data.forEach(dataPoint => {
        const periodKey = dataPoint.period.period_key
        if (aggregatedData[periodKey]) {
          aggregatedData[periodKey].metrics.store_count = max_store_count(aggregatedData[periodKey].metrics.store_count, dataPoint.metrics.store_count)
          aggregatedData[periodKey].metrics.volume_dollars += dataPoint.metrics.volume_dollars
        }
      })
    }
  })

  return Object.values(aggregatedData).sort((a, b) => 
    a.period.period_key.localeCompare(b.period.period_key)
  )
}

/**
 * Determines if a node should be included in aggregation based on target level
 */
function shouldIncludeNode(node: SalesHierarchyNode, targetLevel: HierarchyLevel): boolean {
  const levelOrder = [
    HierarchyLevel.CLASS_OF_TRADE,
    HierarchyLevel.DISTRIBUTOR,
    HierarchyLevel.DISTRIBUTION_CENTER,
    HierarchyLevel.RETAILER,
    HierarchyLevel.PRODUCT_LINE
  ]

  const nodeLevelIndex = levelOrder.indexOf(node.level)
  const targetLevelIndex = levelOrder.indexOf(targetLevel)

  // Include nodes at the target level and all levels below it
  return nodeLevelIndex >= targetLevelIndex
}

/**
 * Builds a hierarchical tree structure from flat node data
 */
export function buildHierarchyTree(nodes: SalesHierarchyNode[]): SalesHierarchyNode[] {
  // If nodes already have children structure, return as-is
  if (nodes.length > 0 && nodes[0].children) {
    return nodes
  }

  const nodeMap = new Map<string, SalesHierarchyNode>()
  const rootNodes: SalesHierarchyNode[] = []

  // Create a map of all nodes
  nodes.forEach(node => {
    nodeMap.set(node.id, { ...node, children: [] })
  })

  // Build parent-child relationships
  nodes.forEach(node => {
    const nodeWithChildren = nodeMap.get(node.id)!
    
    if (node.parent_id) {
      const parent = nodeMap.get(node.parent_id)
      if (parent) {
        parent.children = parent.children || []
        parent.children.push(nodeWithChildren)
        parent.has_children = true
      }
    } else {
      // This is a root node
      rootNodes.push(nodeWithChildren)
    }
  })

  return rootNodes
}

/**
 * Flattens hierarchical tree into a flat array for table display
 * Maintains proper indentation and visibility based on expanded state
 */
const DEFAULT_TABLE_COLUMN_CONFIG: ColumnConfig = {
  show_stores: true,
  show_cases: true,
  show_volume: true,
  quantity_mode: "cases",
}

export function flattenHierarchyForTable(
  nodes: any[],
  expandedNodes: Set<string>,
  visibleLevels: Set<HierarchyLevel>,
  depth: number = 0,
  columnConfig: ColumnConfig = DEFAULT_TABLE_COLUMN_CONFIG
): TableRow[] {
  const flatRows: TableRow[] = []
  const cfg = columnConfig

  nodes.forEach(node => {
    const isExpanded = expandedNodes.has(node.id)
    const isVisible = visibleLevels.has(node.level)
    
    
    if (isVisible) {
      // Calculate monthly data and totals
      const monthlyData = build_monthly_data_from_points(node.sales_data, cfg)
      const totals = calculate_totals_from_points(node.sales_data, cfg)

      flatRows.push({
        id: node.id,
        name: node.name,
        level: node.level,
        depth,
        parent_id: node.parent_id,
        is_expanded: isExpanded,
        is_visible: true,
        has_children: (node.children?.length || 0) > 0 || 
                      (node.retailer_relationships?.length || 0) > 0 || 
                      (node.dc_relationships?.length || 0) > 0,
        monthly_data: monthlyData,
        totals,
        forecast_config: node.forecast_config,
        case_cost: node.case_cost,
        metadata: node.metadata,
        product_sku: node.product_sku,
        sku_name: node.sku_name,
        source_ids: node.source_ids,
      })
    }

    // Recursively process children if expanded
    if (isExpanded) {
      // Handle traditional children structure
      if (node.children) {
        const childRows = flattenHierarchyForTable(
          node.children,
          expandedNodes,
          visibleLevels,
          depth + 1,
          columnConfig
        )
        flatRows.push(...childRows)
      }
      
      // Handle DC-retailer relationships (for DC View)
      if (node.retailer_relationships) {
        node.retailer_relationships.forEach((relationship: any) => {
          const relationshipId = `${node.id}-${relationship.retailer_id}`
          const relationshipRow = {
            id: relationshipId, // Unique ID combining DC and retailer
            name: relationship.retailer_name,
            level: HierarchyLevel.RETAILER,
            depth: depth + 1,
            parent_id: node.id,
            is_expanded: false,
            is_visible: visibleLevels.has(HierarchyLevel.RETAILER),
            has_children: (relationship.children?.length || 0) > 0,
            monthly_data: build_monthly_data_from_points(relationship.sales_data, cfg),
            totals: calculate_totals_from_points(relationship.sales_data, cfg),
            forecast_config: relationship.forecast_config || node.forecast_config,
            case_cost: relationship.case_cost,
            // Store the original IDs for data updates
            original_retailer_id: relationship.retailer_id,
            original_dc_id: node.id
          }
          flatRows.push(relationshipRow)
          
          // Handle children within relationships (product lines under retailers)
          if (relationship.children && relationship.children.length > 0) {
            const childRows = flattenHierarchyForTable(
              relationship.children,
              expandedNodes,
              visibleLevels,
              depth + 2,
              columnConfig
            )
            flatRows.push(...childRows)
          }
        })
      }
      
      // Handle retailer-DC relationships (for Retailer View)
      if (node.dc_relationships) {
        const relationshipRows = node.dc_relationships.map((relationship: any) => {
          const relationshipId = `${node.id}-${relationship.dc_id}`
          return {
            id: relationshipId, // Unique ID combining retailer and DC
            name: relationship.dc_name,
            level: HierarchyLevel.DISTRIBUTION_CENTER,
            depth: depth + 1,
            parent_id: node.id,
            is_expanded: false,
            is_visible: visibleLevels.has(HierarchyLevel.DISTRIBUTION_CENTER),
            has_children: false,
            sales_data: relationship.sales_data || [],
            forecast_config: relationship.forecast_config || node.forecast_config,
            monthly_data: build_monthly_data_from_points(relationship.sales_data, cfg),
            totals: calculate_totals_from_points(relationship.sales_data, cfg),
            case_cost: relationship.case_cost,
            // Store the original IDs for data updates
            original_retailer_id: node.id,
            original_dc_id: relationship.dc_id
          }
        })
        flatRows.push(...relationshipRows)
      }
    }
  })

  return flatRows
}

/**
 * Filters nodes based on data access permissions
 */
export function filterNodesByAccessLevel(
  nodes: SalesHierarchyNode[],
  maxGranularityLevel: HierarchyLevel
): SalesHierarchyNode[] {
  const levelOrder = [
    HierarchyLevel.CLASS_OF_TRADE,
    HierarchyLevel.DISTRIBUTOR,
    HierarchyLevel.DISTRIBUTION_CENTER,
    HierarchyLevel.RETAILER,
    HierarchyLevel.PRODUCT_LINE
  ]

  const maxLevelIndex = levelOrder.indexOf(maxGranularityLevel)

  return nodes.filter(node => {
    const nodeLevelIndex = levelOrder.indexOf(node.level)
    return nodeLevelIndex <= maxLevelIndex
  })
}

/**
 * Generates hierarchy path for a given node
 */
export function getHierarchyPath(node: SalesHierarchyNode, allNodes: SalesHierarchyNode[]): HierarchyPath {
  const path: HierarchyPath = {
    class_of_trade: '',
    distributor: undefined,
    distribution_center: undefined,
    retailer: undefined,
    product_line: undefined
  }

  let currentNode: SalesHierarchyNode | undefined = node

  while (currentNode) {
    switch (currentNode.level) {
      case HierarchyLevel.CLASS_OF_TRADE:
        path.class_of_trade = currentNode.name
        break
      case HierarchyLevel.DISTRIBUTOR:
        path.distributor = currentNode.name
        break
      case HierarchyLevel.DISTRIBUTION_CENTER:
        path.distribution_center = currentNode.name
        break
      case HierarchyLevel.RETAILER:
        path.retailer = currentNode.name
        break
      case HierarchyLevel.PRODUCT_LINE:
        path.product_line = currentNode.name
        break
    }

    currentNode = currentNode.parent_id 
      ? allNodes.find(n => n.id === currentNode!.parent_id)
      : undefined
  }

  return path
}

/**
 * Calculates growth rates between periods
 */
export function calculateGrowthRates(
  salesData: SalesDataPoint[],
  periods: TimePeriod[]
): { [periodKey: string]: { store_growth: number; volume_growth: number } } {
  const growthRates: { [periodKey: string]: { store_growth: number; volume_growth: number } } = {}

  for (let i = 1; i < periods.length; i++) {
    const currentPeriod = periods[i]
    const previousPeriod = periods[i - 1]

    const currentData = salesData.find(d => d.period.period_key === currentPeriod.period_key)
    const previousData = salesData.find(d => d.period.period_key === previousPeriod.period_key)

    if (currentData && previousData) {
      const storeGrowth = previousData.metrics.store_count > 0
        ? ((currentData.metrics.store_count - previousData.metrics.store_count) / previousData.metrics.store_count) * 100
        : 0

      const volumeGrowth = previousData.metrics.volume_dollars > 0
        ? ((currentData.metrics.volume_dollars - previousData.metrics.volume_dollars) / previousData.metrics.volume_dollars) * 100
        : 0

      growthRates[currentPeriod.period_key] = {
        store_growth: storeGrowth,
        volume_growth: volumeGrowth
      }
    }
  }

  return growthRates
}

/**
 * Aggregates sales data from children to parents in the hierarchy
 * This handles both traditional children and relationship-based data structures
 */
export function aggregateChildrenToParents(nodes: any[]): any[] {
  function aggregateNode(node: any): any {
    const aggregatedNode = { ...node }
    
    // First, handle relationship-based aggregation (DCs and Retailers)
    // Handle DC-retailer relationships
    if (node.retailer_relationships && node.retailer_relationships.length > 0) {
      const aggregatedSalesData: { [periodKey: string]: SalesDataPoint } = {}

      // Build new relationship objects: assigning sales_data in place would mutate
      // component state shared with previous renders.
      const updatedRelationships = node.retailer_relationships.map((relationship: any) => {
        let updatedRelationship = relationship
        // First aggregate from relationship's children (product lines)
        if (relationship.children && relationship.children.length > 0) {
          const relationshipAggregatedData: { [periodKey: string]: SalesDataPoint } = {}
          relationship.children.forEach((child: any) => {
            if (child.sales_data && child.sales_data.length > 0) {
              child.sales_data.forEach((dataPoint: any) => {
                const periodKey = dataPoint.period.period_key
                if (!relationshipAggregatedData[periodKey]) {
                  relationshipAggregatedData[periodKey] = {
                    period: dataPoint.period,
        metrics: {
          store_count: 0,
          volume_dollars: 0,
          cases_sold: 0,
          case_cost: 0
        },
                    source_kind: dataPoint.source_kind,
                    is_actual: dataPoint.is_actual,
                    is_forecast: dataPoint.is_forecast
                  }
                }
                add_sales_metrics_into_target(
                  relationshipAggregatedData[periodKey].metrics,
                  dataPoint.metrics,
                )
              })
            }
          })
          // Update relationship's sales_data with aggregated data from children
          if (Object.keys(relationshipAggregatedData).length > 0) {
            const rolledUpSalesData = Object.values(relationshipAggregatedData)
              .map((dataPoint) => ({
                ...dataPoint,
                metrics: {
                  ...dataPoint.metrics,
                  // Sum of child PL volumes already reflects each line's stores × cases × that PL's case_cost.
                  // Do not replace with (Σ stores)(Σ cases) × relationship.case_cost — that mixes unrelated
                  // costs, ignores per-line pricing, and zeros volume when relationship.case_cost is unset.
                  volume_dollars: dataPoint.metrics.volume_dollars,
                },
              }))
              .sort((a, b) => a.period.period_key.localeCompare(b.period.period_key))
            updatedRelationship = { ...relationship, sales_data: rolledUpSalesData }
          }
        }

        // Then aggregate relationship data to parent DC
        if (updatedRelationship.sales_data && updatedRelationship.sales_data.length > 0) {
          updatedRelationship.sales_data.forEach((dataPoint: any) => {
            const periodKey = dataPoint.period.period_key
            if (!aggregatedSalesData[periodKey]) {
              aggregatedSalesData[periodKey] = {
                period: dataPoint.period,
        metrics: {
          store_count: 0,
          volume_dollars: 0,
          cases_sold: 0,
          case_cost: 0
        },
                source_kind: dataPoint.source_kind,
                is_actual: dataPoint.is_actual,
                is_forecast: dataPoint.is_forecast
              }
            }
            add_sales_metrics_into_target(aggregatedSalesData[periodKey].metrics, dataPoint.metrics)
          })
        }

        return updatedRelationship
      })
      aggregatedNode.retailer_relationships = updatedRelationships
      
      if (Object.keys(aggregatedSalesData).length > 0) {
        aggregatedNode.sales_data = Object.values(aggregatedSalesData).map(dataPoint => ({
          ...dataPoint,
          metrics: {
            ...dataPoint.metrics,
            // Volume is already calculated as sum of children volumes
            volume_dollars: dataPoint.metrics.volume_dollars
          }
        })).sort((a, b) => 
          a.period.period_key.localeCompare(b.period.period_key)
        )
      }
      
      // Aggregate forecast configs from relationships (weighted average by volume)
      const relationshipsWithConfigs = updatedRelationships.filter((rel: any) => rel.forecast_config)
      if (relationshipsWithConfigs.length > 0) {
        let totalVolume = 0
        let weightedSameStoreSales = 0
        let weightedStoreCount = 0
        
        relationshipsWithConfigs.forEach((relationship: any) => {
          const relationshipVolume = relationship.sales_data.reduce((sum: number, dp: any) => sum + dp.metrics.volume_dollars, 0)
          totalVolume += relationshipVolume
          weightedSameStoreSales += relationship.forecast_config.same_store_sales_multiplier * relationshipVolume
          weightedStoreCount += relationship.forecast_config.store_count_growth_multiplier * relationshipVolume
        })
        
        if (totalVolume > 0) {
          aggregatedNode.forecast_config = {
            same_store_sales_multiplier: weightedSameStoreSales / totalVolume,
            store_count_growth_multiplier: weightedStoreCount / totalVolume
          }
        }
      }
    }
    
    // Handle retailer-DC relationships
    if (node.dc_relationships && node.dc_relationships.length > 0) {
      const aggregatedSalesData: { [periodKey: string]: SalesDataPoint } = {}
      
      node.dc_relationships.forEach((relationship: any) => {
        if (relationship.sales_data && relationship.sales_data.length > 0) {
          relationship.sales_data.forEach((dataPoint: any) => {
            const periodKey = dataPoint.period.period_key
            if (!aggregatedSalesData[periodKey]) {
              aggregatedSalesData[periodKey] = {
                period: dataPoint.period,
        metrics: {
          store_count: 0,
          volume_dollars: 0,
          cases_sold: 0,
          case_cost: 0
        },
                source_kind: dataPoint.source_kind,
                is_actual: dataPoint.is_actual,
                is_forecast: dataPoint.is_forecast
              }
            }
            add_sales_metrics_into_target(aggregatedSalesData[periodKey].metrics, dataPoint.metrics)
          })
        }
      })
      
      if (Object.keys(aggregatedSalesData).length > 0) {
        aggregatedNode.sales_data = Object.values(aggregatedSalesData).map(dataPoint => ({
          ...dataPoint,
          metrics: {
            ...dataPoint.metrics,
            // Volume is already calculated as sum of children volumes
            volume_dollars: dataPoint.metrics.volume_dollars
          }
        })).sort((a, b) => 
          a.period.period_key.localeCompare(b.period.period_key)
        )
      }
      
      // Aggregate forecast configs from DC relationships (weighted average by volume)
      const dcRelationshipsWithConfigs = node.dc_relationships.filter((rel: any) => rel.forecast_config)
      if (dcRelationshipsWithConfigs.length > 0) {
        let totalVolume = 0
        let weightedSameStoreSales = 0
        let weightedStoreCount = 0
        
        dcRelationshipsWithConfigs.forEach((relationship: any) => {
          const relationshipVolume = relationship.sales_data.reduce((sum: number, dp: any) => sum + dp.metrics.volume_dollars, 0)
          totalVolume += relationshipVolume
          weightedSameStoreSales += relationship.forecast_config.same_store_sales_multiplier * relationshipVolume
          weightedStoreCount += relationship.forecast_config.store_count_growth_multiplier * relationshipVolume
        })
        
        if (totalVolume > 0) {
          aggregatedNode.forecast_config = {
            same_store_sales_multiplier: weightedSameStoreSales / totalVolume,
            store_count_growth_multiplier: weightedStoreCount / totalVolume
          }
        }
      }
    }
    
    // Then, handle traditional children structure
    if (node.children && node.children.length > 0) {
      // Recursively aggregate children first
      const aggregatedChildren = node.children.map((child: any) => aggregateNode(child))
      
      // Aggregate sales data from children
      const aggregatedSalesData: { [periodKey: string]: SalesDataPoint } = {}
      
      aggregatedChildren.forEach((child: any) => {
        if (child.sales_data && child.sales_data.length > 0) {
          // Roll up only each child's primary row per period (forecast shadows
          // volume_based_estimate etc.) so parents match the cells they summarize.
          select_primary_sales_rows(child.sales_data).forEach((dataPoint: any) => {
            const periodKey = dataPoint.period.period_key
            if (!aggregatedSalesData[periodKey]) {
              aggregatedSalesData[periodKey] = structuredClone(dataPoint)
            } else {
              aggregatedSalesData[periodKey] = rollupMergeSalesDataPoints(
                aggregatedSalesData[periodKey],
                dataPoint
              )
            }
          })
        }
      })
      
      if (Object.keys(aggregatedSalesData).length > 0) {
        aggregatedNode.sales_data = Object.values(aggregatedSalesData).map(dataPoint => ({
          ...dataPoint,
          metrics: {
            ...dataPoint.metrics,
            // Volume is already calculated as sum of children volumes
            volume_dollars: dataPoint.metrics.volume_dollars
          }
        })).sort((a, b) => 
          a.period.period_key.localeCompare(b.period.period_key)
        )
      }
      
      // Aggregate forecast configs from children (weighted average by volume)
      const childrenWithConfigs = aggregatedChildren.filter((child: any) => child.forecast_config)
      if (childrenWithConfigs.length > 0) {
        // Calculate weighted averages based on total volume
        let totalVolume = 0
        let weightedSameStoreSales = 0
        let weightedStoreCount = 0
        let weightedInitialDoors = 0
        let weightedInitialCases = 0
        let weightedCostPerCase = 0
        
        childrenWithConfigs.forEach((child: any) => {
          const childVolume = child.sales_data.reduce((sum: number, dp: any) => sum + dp.metrics.volume_dollars, 0)
          totalVolume += childVolume
          weightedSameStoreSales += child.forecast_config.same_store_sales_multiplier * childVolume
          weightedStoreCount += child.forecast_config.store_count_growth_multiplier * childVolume
          weightedInitialDoors += (child.forecast_config.initial_doors || 0) * childVolume
          weightedInitialCases += (child.forecast_config.initial_cases_per_month || 0) * childVolume
          weightedCostPerCase += (child.forecast_config.cost_per_case || 0) * childVolume
        })
        
        if (totalVolume > 0) {
          aggregatedNode.forecast_config = {
            same_store_sales_multiplier: weightedSameStoreSales / totalVolume,
            store_count_growth_multiplier: weightedStoreCount / totalVolume,
            initial_doors: Math.round(weightedInitialDoors / totalVolume),
            initial_cases_per_month: Math.round(weightedInitialCases / totalVolume),
            cost_per_case: weightedCostPerCase / totalVolume
          }
        }
      }
      
      aggregatedNode.children = aggregatedChildren
    }
    
    return aggregatedNode
  }
  
  return nodes.map(node => aggregateNode(node))
}

/**
 * Updates the display name for a hierarchy node (and sku_name when level is SKU).
 */
export function updateNodeNameInHierarchy(nodes: any[], rowId: string, rawName: string): any[] {
  const trimmed = rawName.trim()
  const nextName = trimmed.length > 0 ? trimmed : "Untitled"

  function updateNode(node: any): any {
    if (node.id === rowId) {
      const updated = { ...node, name: nextName }
      if (node.level === HierarchyLevel.SKU || String(node.level) === "sku") {
        ;(updated as any).sku_name = nextName
      }
      return updated
    }
    if (node.children?.length) {
      return {
        ...node,
        children: node.children.map((child: any) => updateNode(child)),
      }
    }
    return node
  }

  return nodes.map((node: any) => updateNode(node))
}

/** Fields a user can edit in a forecast grid cell. */
export type EditableMetricField = 'store_count' | 'volume_dollars' | 'cases_sold' | 'units_sold' | 'case_cost'

const EDIT_DEFAULT_COLUMN_CONFIG: ColumnConfig = {
  show_stores: true,
  show_cases: true,
  show_volume: true,
}

/** Detail keys that scale with revenue (dollar measures). */
const DETAIL_DOLLAR_KEYS: string[] = [
  FORECAST_DETAIL.consumption_gross_sales,
  FORECAST_DETAIL.gross_sales_delivered,
]
/** Detail keys that scale with physical quantity (cases / units measures, incl. promomash base/incremental/total). */
const DETAIL_QUANTITY_KEYS: string[] = [
  FORECAST_DETAIL.forecasted_consumption_cases,
  FORECAST_DETAIL.forecasted_delivery_cases,
  FORECAST_DETAIL.forecasted_consumption_units,
  FORECAST_DETAIL.seasonality_baseline_units,
  FORECAST_DETAIL.incremental_promo_units,
  FORECAST_DETAIL.total_promotion_lift,
  'base',
  'incremental',
  'total',
]

function scale_detail_keys(detail: Record<string, number>, keys: string[], factor: number): void {
  for (const key of keys) {
    const v = detail[key]
    if (typeof v === 'number' && Number.isFinite(v)) {
      detail[key] = v * factor
    }
  }
}

/**
 * Applies a single cell edit to one data point, keeping every layer the grid can
 * display consistent: the canonical `metrics` AND the retailer/promomash
 * `metrics.detail` breakdown (the grid shows detail-slice numbers whenever a row
 * has them, so editing only `metrics` looks like the edit did nothing).
 *
 * Quantity edits (stores / cases / units) scale all volume-proportional measures
 * by `new / old` of the *displayed* value — this preserves the row's own implied
 * pricing and works for planned rows, imports with total cases, and detail rows
 * alike. When the row has no usable baseline (displayed quantity or volume is 0),
 * dollars are populated from the explicit cost instead (`metrics.case_cost`,
 * falling back to the node-level cost). A volume edit sets the displayed dollar
 * basis directly and touches nothing else.
 */
export function apply_metric_edit_to_data_point(
  dataPoint: any,
  field: Exclude<EditableMetricField, 'case_cost'>,
  value: number,
  node_case_cost: number = 0,
  column_config?: ColumnConfig,
): any {
  const cfg = column_config ?? EDIT_DEFAULT_COLUMN_CONFIG
  const metrics = { ...(dataPoint.metrics || {}) }
  const detail: Record<string, number> | undefined = metrics.detail ? { ...metrics.detail } : undefined
  if (detail) {
    metrics.detail = detail
  }
  // What the user actually sees in the cell (detail slice when present, else canonical).
  const displayed = resolve_display_metrics({ metrics } as any, cfg)
  const dollar_basis = cfg.forecast_dollar_basis ?? FORECAST_DETAIL.consumption_gross_sales
  const case_basis = cfg.forecast_case_basis ?? FORECAST_DETAIL.forecasted_consumption_cases
  const unit_basis = cfg.forecast_unit_basis ?? FORECAST_DETAIL.forecasted_consumption_units

  function scale_dollars(factor: number): void {
    metrics.volume_dollars = Number(metrics.volume_dollars ?? 0) * factor
    if (detail) {
      scale_detail_keys(detail, DETAIL_DOLLAR_KEYS, factor)
    }
  }

  function scale_quantities(factor: number): void {
    if (Number(metrics.cases_sold ?? 0) > 0) {
      metrics.cases_sold = round_cases(Number(metrics.cases_sold) * factor)
    }
    if (Number(metrics.units_sold ?? 0) > 0) {
      metrics.units_sold = Math.round(Number(metrics.units_sold) * factor)
    }
    if (detail) {
      scale_detail_keys(detail, DETAIL_QUANTITY_KEYS, factor)
    }
  }

  if (field === 'volume_dollars') {
    // The user edited the displayed dollars: set the displayed basis. The canonical
    // volume follows except when a non-default slice (Delivered) is being edited —
    // canonical dollars represent the consumption channel.
    // Cases and units follow the dollars at the row's own implied price (a revenue cell typed
    // over on a planned row used to leave its volume where it was, so stores × cases × cost no
    // longer footed to the revenue shown).
    const old_dollars = Number(displayed.volume_dollars ?? 0)
    if (old_dollars > 0 && Number.isFinite(value) && value >= 0) {
      scale_quantities(value / old_dollars)
    }
    if (detail && typeof detail[dollar_basis] === 'number') {
      detail[dollar_basis] = value
      if (dollar_basis === FORECAST_DETAIL.consumption_gross_sales) {
        metrics.volume_dollars = value
      }
    } else {
      metrics.volume_dollars = value
    }
    return finish()
  }

  if (field === 'store_count') {
    const old_stores = Number(metrics.store_count ?? 0)
    metrics.store_count = value
    if (old_stores > 0 && displayed.volume_dollars > 0) {
      // Planning model: dollars follow store count; cases stay per-store.
      scale_dollars(value / old_stores)
      // A row whose quantity is the account's total (imported plans) holds its per-store rate
      // instead: the quantity moves with the doors, so dollars per unit stay where they were.
      if (point_quantity_basis(dataPoint.metrics) === "total") {
        scale_quantities(value / old_stores)
      }
    } else if (displayed.volume_dollars === 0) {
      populate_dollars_from_cost()
    }
    // old stores 0 with existing revenue: set the count, never rewrite revenue.
    return finish()
  }

  // cases_sold / units_sold
  const old_displayed_qty = field === 'cases_sold' ? displayed.cases_sold : Number(displayed.units_sold ?? 0)
  if (old_displayed_qty > 0 && displayed.volume_dollars > 0) {
    const factor = value / old_displayed_qty
    scale_dollars(factor)
    scale_quantities(factor)
    snap_edited_field()
  } else if (displayed.volume_dollars === 0) {
    metrics[field] = value
    snap_edited_field()
    populate_dollars_from_cost()
  } else {
    // No displayed quantity baseline but the row has revenue: set the quantity
    // and leave the dollars alone (never silently rewrite imported/actual revenue).
    metrics[field] = value
    snap_edited_field()
  }
  return finish()

  /** Pin the edited measure to exactly the typed value (no rounding drift from scaling). */
  function snap_edited_field(): void {
    if (field === 'cases_sold') {
      if (detail && typeof detail[case_basis] === 'number') {
        detail[case_basis] = value
      } else {
        metrics.cases_sold = value
      }
    } else if (field === 'units_sold') {
      if (detail && typeof detail[unit_basis] === 'number') {
        detail[unit_basis] = value
      } else {
        metrics.units_sold = value
      }
    }
  }

  /** No dollars yet — derive them from the explicit case cost when known. */
  function populate_dollars_from_cost(): void {
    const cost = Number(metrics.case_cost ?? 0) > 0 ? Number(metrics.case_cost) : Number(node_case_cost ?? 0)
    const stores = Number(metrics.store_count ?? 0)
    const cases = Number(metrics.cases_sold ?? 0)
    if (cost > 0 && cases > 0 && (field === 'cases_sold' || field === 'store_count')) {
      // Planned rows model volume as stores × cases-per-store × cost; rows without
      // store data treat cases as totals (matches the server's cases × cost).
      metrics.volume_dollars = (stores > 0 ? stores : 1) * cases * cost
    }
  }

  function finish(): any {
    const updated = { ...dataPoint, metrics }
    // Keep opportunity base metrics (the unscaled truth) consistent with the same edit.
    if (dataPoint.opportunity_base_metrics) {
      updated.opportunity_base_metrics = apply_metric_edit_to_data_point(
        { metrics: dataPoint.opportunity_base_metrics },
        field,
        value,
        node_case_cost,
        column_config,
      ).metrics
    }
    return updated
  }
}

/**
 * Updates data in the hierarchy and returns the updated hierarchy
 */
export function updateDataInHierarchy(
  nodes: any[],
  rowId: string,
  periodKey: string,
  field: Exclude<EditableMetricField, 'case_cost'>,
  value: number,
  column_config?: ColumnConfig
): any[] {
  // A row with children has no row of its own to edit: the typed total is spread over the leaves
  // beneath it (see `spread_parent_edit`), and the parent re-derives from them on aggregation.
  const target = find_node_by_id(nodes, rowId)
  if (target && Array.isArray(target.children) && target.children.length > 0) {
    return spread_parent_edit_in_hierarchy(nodes, rowId, periodKey, field, value, column_config)
  }

  function node_cost(owner: any): number {
    return Number(owner?.case_cost || owner?.forecast_config?.cost_per_case || 0)
  }

  function apply_to_sales_data(owner: any): any[] {
    // Edit only the cell's primary row — never shadowed kinds (volume_based_estimate
    // etc.) that share the same period, and never more than one row per edit.
    const primary_rows = new Set(select_primary_sales_rows(owner.sales_data))
    return owner.sales_data.map((dataPoint: any) => {
      if (dataPoint.period.period_key === periodKey && primary_rows.has(dataPoint)) {
        return apply_metric_edit_to_data_point(dataPoint, field, value, node_cost(owner), column_config)
      }
      return dataPoint
    })
  }

  function updateNode(node: any): any {
    const updatedNode = { ...node }

    // Check if this is the node we need to update (for regular nodes)
    if (node.id === rowId) {
      // Update the sales data for this node
      updatedNode.sales_data = apply_to_sales_data(node)
    }

    // Recursively update children
    if (node.children) {
      updatedNode.children = node.children.map((child: any) => updateNode(child))
    }

    // Update relationships - handle unique relationship IDs
    if (node.retailer_relationships) {
      updatedNode.retailer_relationships = node.retailer_relationships.map((relationship: any) => {
        // Check if this is a relationship row ID (format: "dc_id-retailer_id")
        const relationshipId = `${node.id}-${relationship.retailer_id}`
        if (relationshipId === rowId) {
          return {
            ...relationship,
            sales_data: apply_to_sales_data(relationship)
          }
        }

        // Check if this is a product line within the relationship
        if (relationship.children) {
          const updatedChildren = relationship.children.map((child: any) => {
            if (child.id === rowId) {
              return {
                ...child,
                sales_data: apply_to_sales_data(child)
              }
            }
            return child
          })

          return {
            ...relationship,
            children: updatedChildren
          }
        }

        return relationship
      })
    }

    if (node.dc_relationships) {
      updatedNode.dc_relationships = node.dc_relationships.map((relationship: any) => {
        // Check if this is a relationship row ID (format: "retailer_id-dc_id")
        const relationshipId = `${node.id}-${relationship.dc_id}`
        if (relationshipId === rowId) {
          return {
            ...relationship,
            sales_data: apply_to_sales_data(relationship)
          }
        }
        return relationship
      })
    }

    return updatedNode
  }

  return nodes.map(node => updateNode(node))
}

/** Children-based lookup of one node by id (relationship rows have no node of their own). */
function find_node_by_id(nodes: any[], id: string): any | null {
  for (const node of nodes) {
    if (node?.id === id) return node
    const found = node?.children?.length ? find_node_by_id(node.children, id) : null
    if (found) return found
  }
  return null
}

/** One leaf cell under an edited parent, as `spread_parent_edit` sees it. */
export interface SpreadLeaf {
  id: string
  /** The leaf's current value for the edited measure (dollars, cases or units). */
  value: number
  /** A locked or actual leaf keeps its value; the others absorb the change. */
  is_locked?: boolean
  /**
   * How to split a total when every unlocked leaf is at 0 — the leaf's case count on a revenue
   * edit, its revenue on a quantity edit. All-zero weights fall back to an even split.
   */
  weight?: number
}

/**
 * Spread a parent cell's new total over the leaves beneath it.
 *
 * Each unlocked leaf keeps its share of the old total (`new / old`, the same ratio for all), so
 * a customer typed from $100k to $110k lifts every SKU 10%. Locked and actual leaves keep what
 * they have and the rest absorb the whole change. With nothing to scale (every unlocked leaf at
 * 0) the total splits by `weight`, else evenly. The work is done in whole hundredths (cents,
 * hundredths of a case) and the rounding remainder lands on the largest leaf, so the leaves foot
 * to the typed total to the cent — a parent that re-aggregates to $109,999.99 reads as a bug.
 *
 * Returns only the leaves that take a new value; a typed total below what the locked leaves
 * already hold zeroes the unlocked ones rather than going negative.
 */
export function spread_parent_edit(
  leaves: SpreadLeaf[],
  new_total: number,
  decimals: number = 2,
): Map<string, number> {
  const out = new Map<string, number>()
  const scale = Math.pow(10, decimals)
  const unlocked = leaves.filter((leaf: SpreadLeaf) => !leaf.is_locked)
  if (unlocked.length === 0 || !Number.isFinite(new_total)) return out

  const locked_sum = leaves
    .filter((leaf: SpreadLeaf) => leaf.is_locked)
    .reduce((sum: number, leaf: SpreadLeaf) => sum + Number(leaf.value || 0), 0)
  const target_int = Math.max(0, Math.round((new_total - locked_sum) * scale))

  const old_sum = unlocked.reduce((sum: number, leaf: SpreadLeaf) => sum + Math.max(0, Number(leaf.value || 0)), 0)
  const weight_sum = unlocked.reduce((sum: number, leaf: SpreadLeaf) => sum + Math.max(0, Number(leaf.weight || 0)), 0)
  function share(leaf: SpreadLeaf): number {
    if (old_sum > 0) return Math.max(0, Number(leaf.value || 0)) / old_sum
    if (weight_sum > 0) return Math.max(0, Number(leaf.weight || 0)) / weight_sum
    return 1 / unlocked.length
  }

  let allocated = 0
  const allocations = unlocked.map((leaf: SpreadLeaf) => {
    const amount = Math.floor(share(leaf) * target_int)
    allocated += amount
    return { leaf, amount }
  })
  // Remainder to the largest leaf — by current value, then weight, then position — so the sum
  // is exact and no small row is handed a disproportionate rounding bump.
  let largest = allocations[0]
  for (const candidate of allocations) {
    const candidate_value = Number(candidate.leaf.value || 0)
    const largest_value = Number(largest.leaf.value || 0)
    if (
      candidate_value > largest_value ||
      (candidate_value === largest_value && Number(candidate.leaf.weight || 0) > Number(largest.leaf.weight || 0))
    ) {
      largest = candidate
    }
  }
  largest.amount += target_int - allocated

  for (const { leaf, amount } of allocations) {
    out.set(leaf.id, amount / scale)
  }
  return out
}

/** Leaf rows that must not move under a parent edit: actuals, and periods a planner locked. */
function spread_leaf_is_locked(point: any): boolean {
  return point?.is_locked === true || point?.is_actual === true || point?.source_kind === "actual"
}

/**
 * Apply a parent-row edit: find the leaves under `rowId`, spread the typed total over their
 * cells for `periodKey` with `spread_parent_edit`, and write each leaf's new value through the
 * same per-cell edit a leaf would take (`apply_metric_edit_to_data_point`), so dollars, cases
 * and units move together on every leaf. Store counts are never touched. A units edit converts
 * to cases through each leaf's own pack size (`metadata.units_per_case`); a leaf with no pack
 * size and no unit count cannot take a units edit and is left alone.
 */
export function spread_parent_edit_in_hierarchy(
  nodes: any[],
  rowId: string,
  periodKey: string,
  field: Exclude<EditableMetricField, "case_cost">,
  value: number,
  column_config?: ColumnConfig,
): any[] {
  if (field === "store_count") return nodes
  const parent = find_node_by_id(nodes, rowId)
  if (!parent) return nodes
  const cfg = column_config ?? EDIT_DEFAULT_COLUMN_CONFIG

  interface LeafCell {
    node: any
    point: any
    units_per_case: number | null
    displayed: { volume_dollars: number; cases_sold: number; units_sold: number | null }
  }
  const cells: LeafCell[] = []
  function collect(node: any): void {
    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach(collect)
      return
    }
    const primary = select_primary_sales_rows(node.sales_data ?? [])
    const point = primary.find((dp: any) => dp?.period?.period_key === periodKey)
    if (!point) return
    cells.push({ node, point, units_per_case: node_units_per_case(node), displayed: resolve_display_metrics(point, cfg) })
  }
  collect(parent)
  if (cells.length === 0) return nodes

  function leaf_units(cell: LeafCell): number | null {
    if (cell.displayed.units_sold !== null && cell.displayed.units_sold !== undefined) {
      return Number(cell.displayed.units_sold)
    }
    return units_from_cases(cell.displayed.cases_sold, cell.units_per_case)
  }

  const spread_leaves: SpreadLeaf[] = cells.map((cell: LeafCell, index: number) => {
    const id = String(index)
    const is_locked = spread_leaf_is_locked(cell.point)
    if (field === "volume_dollars") {
      return { id, value: cell.displayed.volume_dollars, weight: cell.displayed.cases_sold, is_locked }
    }
    if (field === "cases_sold") {
      return { id, value: cell.displayed.cases_sold, weight: cell.displayed.volume_dollars, is_locked }
    }
    const units = leaf_units(cell)
    // No pack size and no unit count: this leaf cannot be expressed in units, so it holds still.
    return { id, value: units ?? 0, weight: cell.displayed.volume_dollars, is_locked: is_locked || units === null }
  })

  const new_values = spread_parent_edit(spread_leaves, value, field === "units_sold" ? 0 : 2)
  if (new_values.size === 0) return nodes

  const replacements = new Map<any, any>()
  cells.forEach((cell: LeafCell, index: number) => {
    const next = new_values.get(String(index))
    if (next === undefined) return
    const node_cost = Number(cell.node.case_cost || cell.node.forecast_config?.cost_per_case || 0)
    let leaf_field: Exclude<EditableMetricField, "case_cost"> = field
    let leaf_value = next
    if (field === "units_sold") {
      const as_cases = cases_from_units(next, cell.units_per_case)
      if (as_cases !== null) {
        leaf_field = "cases_sold"
        leaf_value = as_cases
      }
    }
    const updated_point = apply_metric_edit_to_data_point(cell.point, leaf_field, leaf_value, node_cost, cfg)
    replacements.set(cell.node, {
      ...cell.node,
      sales_data: cell.node.sales_data.map((dp: any) => (dp === cell.point ? updated_point : dp)),
    })
  })

  function rewrite(node: any): any {
    const replaced = replacements.get(node)
    if (replaced) return replaced
    if (Array.isArray(node.children) && node.children.length > 0) {
      const children = node.children.map(rewrite)
      const changed = children.some((child: any, index: number) => child !== node.children[index])
      return changed ? { ...node, children } : node
    }
    return node
  }
  return nodes.map(rewrite)
}

/**
 * Generates forecasts for the next calendar year based on multipliers
 */
export function generateForecasts(
  nodes: any[],
  forecastConfig: any
): any[] {
  if (!forecastConfig.enabled) {
    return nodes
  }

  function generateNodeForecasts(node: any): any {
    const updatedNode = { ...node }
    
    // Generate forecasts for this node's sales data
    if (node.sales_data && node.sales_data.length > 0) {
      const lastActualPeriod = node.sales_data
        .filter((dp: any) => !dp.is_forecast)
        .sort((a: any, b: any) => b.period.period_key.localeCompare(a.period.period_key))[0]
      
      if (lastActualPeriod) {
        const nodeForecastConfig = node.forecast_config || forecastConfig
        const forecastData = generateForecastData(
          lastActualPeriod,
          nodeForecastConfig
        )
        
        // Replace existing forecast data with new forecasts
        updatedNode.sales_data = node.sales_data.map((dp: any) => {
          if (dp.is_forecast) {
            const forecastPeriod = forecastData.find((f: any) => f.period.period_key === dp.period.period_key)
            return forecastPeriod || dp
          }
          return dp
        })
      }
    }
    
    // Recursively process children
    if (node.children) {
      updatedNode.children = node.children.map((child: any) => generateNodeForecasts(child))
    }
    
    // Process relationships
    if (node.retailer_relationships) {
      updatedNode.retailer_relationships = node.retailer_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }
        
        // Generate forecasts for relationship's children (product lines)
        if (relationship.children) {
          updatedRelationship.children = relationship.children.map((child: any) => generateNodeForecasts(child))
        }
        
        // Generate forecasts for relationship's sales data
        if (relationship.sales_data && relationship.sales_data.length > 0) {
          const lastActualPeriod = relationship.sales_data
            .filter((dp: any) => !dp.is_forecast)
            .sort((a: any, b: any) => b.period.period_key.localeCompare(a.period.period_key))[0]
          
          if (lastActualPeriod) {
            const relationshipForecastConfig = relationship.forecast_config || node.forecast_config || forecastConfig
            const forecastData = generateForecastData(
              lastActualPeriod,
              relationshipForecastConfig
            )
            
            updatedRelationship.sales_data = relationship.sales_data.map((dp: any) => {
              if (dp.is_forecast) {
                const forecastPeriod = forecastData.find((f: any) => f.period.period_key === dp.period.period_key)
                return forecastPeriod || dp
              }
              return dp
            })
          }
        }
        
        return updatedRelationship
      })
    }
    
    if (node.dc_relationships) {
      updatedNode.dc_relationships = node.dc_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }
        
        // Generate forecasts for relationship's sales data
        if (relationship.sales_data && relationship.sales_data.length > 0) {
          const lastActualPeriod = relationship.sales_data
            .filter((dp: any) => !dp.is_forecast)
            .sort((a: any, b: any) => b.period.period_key.localeCompare(a.period.period_key))[0]
          
          if (lastActualPeriod) {
            const relationshipForecastConfig = relationship.forecast_config || node.forecast_config || forecastConfig
            const forecastData = generateForecastData(
              lastActualPeriod,
              relationshipForecastConfig
            )
            
            updatedRelationship.sales_data = relationship.sales_data.map((dp: any) => {
              if (dp.is_forecast) {
                const forecastPeriod = forecastData.find((f: any) => f.period.period_key === dp.period.period_key)
                return forecastPeriod || dp
              }
              return dp
            })
          }
        }
        
        return updatedRelationship
      })
    }
    
    return updatedNode
  }
  
  return nodes.map(node => generateNodeForecasts(node))
}

export function calculateNextForecastMetrics(
  previousPeriodData: { store_count: number; cases_sold: number; volume_dollars: number },
  forecastConfig: { store_count_growth_multiplier: number; same_store_sales_multiplier: number }
) {
  return {
    store_count: Math.round(previousPeriodData.store_count * (1 + forecastConfig.store_count_growth_multiplier)),
    volume_dollars: Math.round(previousPeriodData.volume_dollars * (1 + forecastConfig.same_store_sales_multiplier)),
    cases_sold: round_cases(previousPeriodData.cases_sold * (1 + forecastConfig.same_store_sales_multiplier))
  }
}

/**
 * Regenerates forecasts for a specific row and its children
 */
export function regenerateForecastsForRow(
  nodes: any[],
  targetRowId: string,
  globalForecastConfig: any
): any[] {
  
  function regenerateNodeForecasts(node: any): any {
    const updatedNode = { ...node }
    
    // Check if this is the target node or if targetRowId is a relationship within this node
    const isTargetNode = node.id === targetRowId
    const isTargetRelationship = targetRowId.includes('-') && targetRowId.startsWith(node.id + '-')
    
    // Generate forecasts for this node's sales data (only if this is the target node)
    if (isTargetNode && node.sales_data && node.sales_data.length > 0) {
      const lastActualPeriod = node.sales_data
        .filter((dp: any) => !dp.is_forecast)
        .sort((a: any, b: any) => b.period.period_key.localeCompare(a.period.period_key))[0]
      
      if (lastActualPeriod) {
        const nodeForecastConfig = node.forecast_config || globalForecastConfig
        
        // Pass all sales data to identify future periods
        const forecastData = generateForecastData(
          lastActualPeriod,
          nodeForecastConfig,
          node.sales_data
        )
        
        // Replace existing forecast data with new forecasts
        updatedNode.sales_data = node.sales_data.map((dp: any) => {
          if (dp.is_forecast) {
            const dpPeriodKey = dp.period.period_key || dp.period_key
            const forecastPeriod = forecastData.find((f: any) => f.period.period_key === dpPeriodKey)
            return forecastPeriod || dp
          }
          return dp
        })
      }
    }
    
    // Recursively process children
    if (node.children) {
      updatedNode.children = node.children.map((child: any) => regenerateNodeForecasts(child))
    }
    
    // Process relationships
    if (node.retailer_relationships) {
      updatedNode.retailer_relationships = node.retailer_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }
        
        // Check if this is the target relationship
        const relationshipId = `${node.id}-${relationship.retailer_id}`
        const isTargetRelationship = relationshipId === targetRowId
        
        // Process relationships - if this is the target relationship, regenerate its forecasts
        if (isTargetRelationship && relationship.sales_data && relationship.sales_data.length > 0) {
          const lastActualPeriod = relationship.sales_data
            .filter((dp: any) => !dp.is_forecast)
            .sort((a: any, b: any) => b.period.period_key.localeCompare(a.period.period_key))[0]
          
          if (lastActualPeriod) {
            // Use the updated relationship config - this should be the row-level multiplier
            const relationshipForecastConfig = relationship.forecast_config
            
            // Use the relationship's config directly - this is the row-level multiplier
            let finalConfig = relationshipForecastConfig
            if (
              !relationshipForecastConfig
              || relationshipForecastConfig.same_store_sales_multiplier == null
              || relationshipForecastConfig.store_count_growth_multiplier == null
            ) {
              finalConfig = {
                same_store_sales_multiplier: globalForecastConfig.same_store_sales_multiplier ?? 0.03,
                store_count_growth_multiplier: globalForecastConfig.store_count_growth_multiplier ?? 0.02
              }
            }
            
            
            const forecastData = generateForecastData(
              lastActualPeriod,
              finalConfig,
              relationship.sales_data
            )
            
            updatedRelationship.sales_data = relationship.sales_data.map((dp: any) => {
              if (dp.is_forecast) {
                const forecastPeriod = forecastData.find((f: any) => f.period.period_key === dp.period.period_key)
                if (forecastPeriod) {
                  return forecastPeriod
                }
              }
              return dp
            })
            
          }
        }
        
        // If we processed a target relationship, we also need to update the parent node's aggregated data
        if (isTargetRelationship) {
          // Mark that this node needs re-aggregation
          updatedNode._needsReaggregation = true
        }
        
        // Generate forecasts for relationship's children (product lines)
        if (relationship.children) {
          updatedRelationship.children = relationship.children.map((child: any) => regenerateNodeForecasts(child))
        }
        
        
        return updatedRelationship
      })
    }
    
    if (node.dc_relationships) {
      updatedNode.dc_relationships = node.dc_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }
        
        // Check if this is the target relationship
        const relationshipId = `${node.id}-${relationship.dc_id}`
        const isTargetRelationship = relationshipId === targetRowId
        
        // Generate forecasts for relationship's sales data (only if this is the target relationship)
        if (isTargetRelationship && relationship.sales_data && relationship.sales_data.length > 0) {
          const lastActualPeriod = relationship.sales_data
            .filter((dp: any) => !dp.is_forecast)
            .sort((a: any, b: any) => b.period.period_key.localeCompare(a.period.period_key))[0]
          
          if (lastActualPeriod) {
            const relationshipForecastConfig = relationship.forecast_config || node.forecast_config || globalForecastConfig
            const forecastData = generateForecastData(
              lastActualPeriod,
              relationshipForecastConfig,
              relationship.sales_data
            )
            
            updatedRelationship.sales_data = relationship.sales_data.map((dp: any) => {
              if (dp.is_forecast) {
                const forecastPeriod = forecastData.find((f: any) => f.period.period_key === dp.period.period_key)
                return forecastPeriod || dp
              }
              return dp
            })
          }
        }
        
        return updatedRelationship
      })
    }
    
    // If this node needs re-aggregation (because one of its relationships was updated),
    // re-aggregate its sales data from its relationships
    if (updatedNode._needsReaggregation && updatedNode.retailer_relationships) {
      
      // Aggregate sales data from all relationships
      const allPeriods = new Set<string>()
      updatedNode.retailer_relationships.forEach((rel: any) => {
        rel.sales_data.forEach((dp: any) => {
          allPeriods.add(dp.period.period_key)
        })
      })
      
      const aggregatedSalesData: any[] = []
      allPeriods.forEach(periodKey => {
        const periodData = updatedNode.retailer_relationships.reduce(
          (acc: SalesMetrics & { is_forecast: boolean }, rel: any) => {
            const dataPoint = rel.sales_data.find((dp: any) => dp.period.period_key === periodKey)
            if (dataPoint) {
              add_sales_metrics_into_target(acc, dataPoint.metrics)
              acc.is_forecast = dataPoint.is_forecast
            }
            return acc
          },
          {
            store_count: 0,
            volume_dollars: 0,
            cases_sold: 0,
            case_cost: 0,
            is_forecast: false,
          },
        )
        
        // Find the period object from any relationship
        const sampleDataPoint = updatedNode.retailer_relationships.find((rel: any) => 
          rel.sales_data.some((dp: any) => dp.period.period_key === periodKey)
        )?.sales_data.find((dp: any) => dp.period.period_key === periodKey)
        
        if (sampleDataPoint) {
          const { is_forecast, ...rolled_metrics } = periodData
          aggregatedSalesData.push({
            period: sampleDataPoint.period,
            metrics: rolled_metrics,
            is_forecast,
          })
        }
      })
      
      updatedNode.sales_data = aggregatedSalesData.sort((a, b) => a.period.period_key.localeCompare(b.period.period_key))
    }
    
    return updatedNode
  }
  
  return nodes.map(node => regenerateNodeForecasts(node))
}

/**
 * Generates forecast data points based on the last actual period and forecast config
 */
function generateForecastData(
  initialLastActualPeriod: any,
  forecastConfig: any,
  allSalesData: any[] = []
): any[] {
  if (
    !forecastConfig
    || forecastConfig.same_store_sales_multiplier == null
    || forecastConfig.store_count_growth_multiplier == null
  ) {
    console.error('Invalid forecast config:', forecastConfig)
    return []
  }
  
  const forecasts: any[] = []
  
  // Get current date to determine which periods are in the future
  const currentDate = new Date()
  const currentPeriodKey = format(currentDate, 'yyyy-MM')
  
  // Helper function to extract period key from various data structures
  function extractPeriodKey(dataPoint: any): string | null {
    if (dataPoint.period_key) return dataPoint.period_key
    if (dataPoint.period?.period_key) return dataPoint.period.period_key
    if (dataPoint.period && typeof dataPoint.period === 'string') return dataPoint.period
    return null
  }
  
  // Sort all sales data by period to ensure we process in chronological order
  const allSalesDataSorted = allSalesData.sort((a: any, b: any) => {
    const aKey = extractPeriodKey(a) || ''
    const bKey = extractPeriodKey(b) || ''
    return aKey.localeCompare(bKey)
  })
  
  // Find the last actual period (not forecast)
  const lastActualPeriod = allSalesDataSorted
    .filter((dp: any) => !dp.is_forecast)
    .pop()
  
  if (!lastActualPeriod) {
    console.error('No actual period found for forecasting')
    return []
  }
  
  // Find all future periods from the existing sales data
  const futurePeriods = allSalesDataSorted.filter(dataPoint => {
    const dataPeriodKey = extractPeriodKey(dataPoint)
    return dataPeriodKey && dataPeriodKey > currentPeriodKey
  })
  
  
  // If no future periods found in existing data, generate them based on the last actual period
  let periodsToForecast = futurePeriods
  if (periodsToForecast.length === 0) {
    console.warn('No future periods found in existing data, generating forecast periods')
    
    // Generate forecast periods for the next 12 months from the last actual period
    const lastPeriodKey = extractPeriodKey(lastActualPeriod)
    if (lastPeriodKey) {
      const [lastYear, lastMonth] = lastPeriodKey.split('-').map(Number)
      periodsToForecast = []
      
      
      for (let i = 1; i <= 12; i++) {
        let forecastYear = lastYear
        let forecastMonth = lastMonth + i
        
        // Handle year rollover
        while (forecastMonth > 12) {
          forecastMonth -= 12
          forecastYear += 1
        }
        
        const periodKey = `${forecastYear}-${forecastMonth.toString().padStart(2, '0')}`
        
        // Only include periods that are in the future
        if (periodKey > currentPeriodKey) {
          periodsToForecast.push({
            period_key: periodKey,
            period: { period_key: periodKey, year: forecastYear, month: forecastMonth }
          })
        }
      }
      
    }
  }
  
  // Generate forecasts for future periods only, using previous month as base
  let previousPeriodData = lastActualPeriod.metrics
  
  periodsToForecast.forEach((forecastPeriod, index) => {
    const periodKey = extractPeriodKey(forecastPeriod)
    if (!periodKey) return
    
    const [forecastYear, forecastMonth] = periodKey.split('-').map(Number)
    
    // Apply multipliers to previous month's data
    const forecastMetrics = calculateNextForecastMetrics(previousPeriodData, forecastConfig)
    
    const forecastPoint = {
      period: {
        period_key: periodKey,
        year: forecastYear,
        month: forecastMonth
      },
      metrics: forecastMetrics,
      is_forecast: true
    }
    
    forecasts.push(forecastPoint)
    
    // Update previous period data for next iteration
    previousPeriodData = forecastMetrics
  })
  
  return forecasts
}

/**
 * Updates forecast configuration for a specific row in the hierarchy
 */
export function updateForecastConfigInHierarchy(
  nodes: any[],
  rowId: string,
  newConfig: { same_store_sales_multiplier: number, store_count_growth_multiplier: number }
): any[] {
  function updateNode(node: any): any {
    const updatedNode = { ...node }
    
    // Check if this is the node we want to update. Merge — replacing would drop
    // initial_doors / initial_cases_per_month / cost_per_case from the saved config.
    if (node.id === rowId) {
      updatedNode.forecast_config = { ...(node.forecast_config || {}), ...newConfig }
    }

    // Recursively process children
    if (node.children) {
      updatedNode.children = node.children.map((child: any) => updateNode(child))
    }

    // Process relationships
    if (node.retailer_relationships) {
      updatedNode.retailer_relationships = node.retailer_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }

        // Check if this is a relationship we want to update
        const relationshipId = `${node.id}-${relationship.retailer_id}`
        if (relationshipId === rowId) {
          updatedRelationship.forecast_config = { ...(relationship.forecast_config || {}), ...newConfig }
        }

        // Process relationship children (product lines)
        if (relationship.children) {
          updatedRelationship.children = relationship.children.map((child: any) => updateNode(child))
        }

        return updatedRelationship
      })
    }

    if (node.dc_relationships) {
      updatedNode.dc_relationships = node.dc_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }

        // Check if this is a relationship we want to update
        const relationshipId = `${node.id}-${relationship.dc_id}`
        if (relationshipId === rowId) {
          updatedRelationship.forecast_config = { ...(relationship.forecast_config || {}), ...newConfig }
        }

        return updatedRelationship
      })
    }
    
    return updatedNode
  }
  
  return nodes.map(node => updateNode(node))
}


/**
 * Sets a new case cost on one data point and reprices its dollars.
 * Actual and locked rows keep their revenue (their dollars are facts, not plans);
 * everything else scales proportionally from the previous cost, or is computed
 * from stores × cases × cost when there is no usable baseline.
 */
export function reprice_data_point_for_case_cost(dataPoint: any, newCaseCost: number): any {
  const metrics = { ...(dataPoint.metrics || {}) }
  const old_cost = Number(metrics.case_cost ?? 0)
  metrics.case_cost = newCaseCost

  const is_actual_row = dataPoint.source_kind === 'actual' || dataPoint.is_actual === true
  if (!is_actual_row && !dataPoint.is_locked) {
    const old_volume = Number(metrics.volume_dollars ?? 0)
    const stores = Number(metrics.store_count ?? 0)
    const cases = Number(metrics.cases_sold ?? 0)
    if (old_cost > 0 && old_volume > 0) {
      metrics.volume_dollars = old_volume * (newCaseCost / old_cost)
    } else if (cases > 0 && newCaseCost > 0) {
      metrics.volume_dollars = (stores > 0 ? stores : 1) * cases * newCaseCost
    }
  }

  return {
    ...dataPoint,
    metrics,
    opportunity_base_metrics: dataPoint.opportunity_base_metrics
      ? reprice_data_point_for_case_cost({ metrics: dataPoint.opportunity_base_metrics }, newCaseCost).metrics
      : dataPoint.opportunity_base_metrics,
  }
}

/**
 * Updates case_cost for a specific row in the hierarchy
 */
export function updateCaseCostInHierarchy(nodes: any[], rowId: string, newCaseCost: number): any[] {
  const target_opportunity_id = findOpportunityIdForNode(nodes, rowId)

  function shouldUpdateNode(node: any, withinTargetSubtree: boolean): boolean {
    if (withinTargetSubtree) {
      return true
    }
    if (node.id === rowId) {
      return true
    }
    if (target_opportunity_id && node.metadata?.opportunity?.id === target_opportunity_id) {
      return true
    }
    return false
  }

  function updateForecastConfigCost(updatedNode: any): void {
    if (updatedNode.forecast_config) {
      updatedNode.forecast_config = {
        ...updatedNode.forecast_config,
        cost_per_case: newCaseCost
      }
    }
  }

  function updateSalesDataCost(updatedNode: any): void {
    if (Array.isArray(updatedNode.sales_data)) {
      updatedNode.sales_data = updatedNode.sales_data.map((dataPoint: any) => reprice_data_point_for_case_cost(dataPoint, newCaseCost))
    }
  }

  function applyCaseCost(updatedNode: any): void {
    updatedNode.case_cost = newCaseCost
    updateForecastConfigCost(updatedNode)
    updateSalesDataCost(updatedNode)
  }

  function updateRelationshipCaseCost(relationship: any, relationshipId: string, withinTargetSubtree: boolean): any {
    const updatedRelationship = { ...relationship }
    const matchesTarget = relationshipId === rowId
    const should_update = withinTargetSubtree || matchesTarget
    if (should_update) {
      updatedRelationship.case_cost = newCaseCost
      if (updatedRelationship.forecast_config) {
        updatedRelationship.forecast_config = {
          ...updatedRelationship.forecast_config,
          cost_per_case: newCaseCost,
        }
      }
      if (Array.isArray(updatedRelationship.sales_data)) {
        updatedRelationship.sales_data = updatedRelationship.sales_data.map((dataPoint: any) =>
          reprice_data_point_for_case_cost(dataPoint, newCaseCost)
        )
      }
    }
    if (Array.isArray(relationship.children)) {
      updatedRelationship.children = relationship.children.map((child: any) => updateNode(child, should_update))
    }
    return updatedRelationship
  }

  function updateNode(node: any, withinTargetSubtree: boolean = false): any {
    const updatedNode = { ...node }
    const nodeMatchesTarget = node.id === rowId
    const should_update = shouldUpdateNode(node, withinTargetSubtree)
    if (should_update) {
      applyCaseCost(updatedNode)
    }
    
    // Update children
    if (node.children) {
      updatedNode.children = node.children.map((child: any) => updateNode(child, should_update || nodeMatchesTarget))
    }
    
    // Update retailer relationships
    if (node.retailer_relationships) {
      updatedNode.retailer_relationships = node.retailer_relationships.map((relationship: any) =>
        updateRelationshipCaseCost(
          relationship,
          `${node.id}-${relationship.retailer_id}`,
          should_update || nodeMatchesTarget
        )
      )
    }
    
    // Update DC relationships
    if (node.dc_relationships) {
      updatedNode.dc_relationships = node.dc_relationships.map((relationship: any) =>
        updateRelationshipCaseCost(
          relationship,
          `${node.id}-${relationship.dc_id}`,
          should_update || nodeMatchesTarget
        )
      )
    }
    
    return updatedNode
  }
  
  return nodes.map((node: any) => updateNode(node))
}

function findOpportunityIdForNode(nodes: any[], rowId: string): string | null {
  function visit(node: any): string | null {
    if (node.id === rowId) {
      return node.metadata?.opportunity?.id || null
    }
    if (Array.isArray(node.children)) {
      for (const child of node.children) {
        const found = visit(child)
        if (found !== null) {
          return found
        }
      }
    }
    if (Array.isArray(node.retailer_relationships)) {
      for (const relationship of node.retailer_relationships) {
        if (`${node.id}-${relationship.retailer_id}` === rowId) {
          return relationship?.metadata?.opportunity?.id || null
        }
        if (Array.isArray(relationship.children)) {
          for (const child of relationship.children) {
            const found = visit(child)
            if (found !== null) {
              return found
            }
          }
        }
      }
    }
    if (Array.isArray(node.dc_relationships)) {
      for (const relationship of node.dc_relationships) {
        if (`${node.id}-${relationship.dc_id}` === rowId) {
          return relationship?.metadata?.opportunity?.id || null
        }
        if (Array.isArray(relationship.children)) {
          for (const child of relationship.children) {
            const found = visit(child)
            if (found !== null) {
              return found
            }
          }
        }
      }
    }
    return null
  }

  for (const node of nodes) {
    const found = visit(node)
    if (found !== null) {
      return found
    }
  }
  return null
}


/**
 * Recalculates volume data when case cost is updated
 * Volume = stores × cases × cost_per_case
 */
export function recalculateVolumeAfterCaseCostUpdate(
  nodes: any[],
  targetRowId: string,
  newCaseCost: number
): any[] {
  function recalculateNode(node: any): any {
    const updatedNode = { ...node }
    
    // Check if this is the target node
    if (node.id === targetRowId) {
      // Recalculate volume for this node's sales data
      if (node.sales_data && node.sales_data.length > 0) {
        updatedNode.sales_data = node.sales_data.map((dataPoint: any) => {
          // Volume = stores × cases × cost_per_case
          const newVolume = dataPoint.metrics.store_count * dataPoint.metrics.cases_sold * newCaseCost
          return {
            ...dataPoint,
            metrics: {
              ...dataPoint.metrics,
              case_cost: newCaseCost, // Update case_cost in the metrics
              volume_dollars: newVolume
            }
          }
        })
      }
    }
    
    // Recursively process children
    if (node.children) {
      updatedNode.children = node.children.map((child: any) => recalculateNode(child))
    }
    
    // Process retailer relationships
    if (node.retailer_relationships) {
      updatedNode.retailer_relationships = node.retailer_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }
        
        // Check if this is the target relationship
        const relationshipId = `${node.id}-${relationship.retailer_id}`
        if (relationshipId === targetRowId) {
          // Recalculate volume for this relationship's sales data
          if (relationship.sales_data && relationship.sales_data.length > 0) {
            updatedRelationship.sales_data = relationship.sales_data.map((dataPoint: any) => {
              const newVolume = dataPoint.metrics.store_count * dataPoint.metrics.cases_sold * newCaseCost
              return {
                ...dataPoint,
                metrics: {
                  ...dataPoint.metrics,
                  case_cost: newCaseCost, // Update case_cost in the metrics
                  volume_dollars: newVolume
                }
              }
            })
          }
        }
        
        // Process relationship children (product lines)
        if (relationship.children) {
          updatedRelationship.children = relationship.children.map((child: any) => recalculateNode(child))
        }
        
        return updatedRelationship
      })
    }
    
    // Process DC relationships
    if (node.dc_relationships) {
      updatedNode.dc_relationships = node.dc_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }
        
        // Check if this is the target relationship
        const relationshipId = `${node.id}-${relationship.dc_id}`
        if (relationshipId === targetRowId) {
          // Recalculate volume for this relationship's sales data
          if (relationship.sales_data && relationship.sales_data.length > 0) {
            updatedRelationship.sales_data = relationship.sales_data.map((dataPoint: any) => {
              const newVolume = dataPoint.metrics.store_count * dataPoint.metrics.cases_sold * newCaseCost
              return {
                ...dataPoint,
                metrics: {
                  ...dataPoint.metrics,
                  volume_dollars: newVolume
                }
              }
            })
          }
        }
        
        return updatedRelationship
      })
    }
    
    return updatedNode
  }
  
  return nodes.map(node => recalculateNode(node))
}


/**
 * Aggregates all sales data by product line across all hierarchy levels
 * This creates a flat list of product lines with aggregated sales data
 * Product lines with the same name are combined into a single entry
 */
export function aggregateByProductLine(nodes: any[]): any[] {
  const productLineMap = new Map<string, any>()
  
  function processNode(node: any) {
    // If this is a product line, aggregate its data
    if (node.level === HierarchyLevel.PRODUCT_LINE) {
      const productLineName = node.name
      const productLineKey = productLineName.toLowerCase().trim() // Use name as key for grouping
      
      if (productLineMap.has(productLineKey)) {
        // Merge with existing product line data
        const existing = productLineMap.get(productLineKey)!
        existing.sales_data = mergeSalesData(existing.sales_data, node.sales_data)
        // Use the most recent case cost and forecast config
        existing.case_cost = node.case_cost || existing.case_cost
        existing.forecast_config = node.forecast_config || existing.forecast_config
        existing.metadata = existing.metadata || node.metadata
        // Track all source IDs for reference
        if (!existing.source_ids) {
          existing.source_ids = [existing.id]
        }
        existing.source_ids.push(node.id)
      } else {
        // Create new product line entry
        productLineMap.set(productLineKey, {
          id: `aggregated-${productLineKey}`, // Use aggregated ID
          name: productLineName,
          level: HierarchyLevel.PRODUCT_LINE,
          depth: 0, // All product lines at same level
          parent_id: undefined,
          children: [],
          sales_data: [...node.sales_data],
          case_cost: node.case_cost,
          forecast_config: node.forecast_config,
          metadata: node.metadata,
          is_expanded: false,
          is_visible: true,
          has_children: false,
          source_ids: [node.id] // Track original source IDs
        })
      }
    }
    
    // Process children
    if (node.children) {
      node.children.forEach((child: any) => processNode(child))
    }
    
    // Process retailer relationships
    if (node.retailer_relationships) {
      node.retailer_relationships.forEach((relationship: any) => {
        if (relationship.children) {
          relationship.children.forEach((child: any) => processNode(child))
        }
      })
    }
    
    // Process DC relationships
    if (node.dc_relationships) {
      node.dc_relationships.forEach((relationship: any) => {
        if (relationship.children) {
          relationship.children.forEach((child: any) => processNode(child))
        }
      })
    }
  }
  
  // Process all nodes
  nodes.forEach(node => processNode(node))
  
  // Convert map to array and sort by name
  return Array.from(productLineMap.values()).sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Separator between the product line key and the SKU name inside an aggregated SKU id.
 * A NUL byte can never occur in a product line or SKU name, so the id splits back into
 * its two parts unambiguously. A '-' separator did not: "Berry-Jam" / "12oz" and
 * "Berry" / "Jam-12oz" produce the same id, and parsing picked the wrong SKU name.
 * These ids only exist in the in-memory table tree (row keys / expanded-node sets),
 * so they are never rendered as DOM ids or persisted.
 */
const AGGREGATED_SKU_ID_SEPARATOR = '\u0000'

/** Grouping key for an aggregated SKU: its product line key plus its own name, both normalized. */
function aggregatedSKUKey(productLineKey: string, skuName: string): string {
  return `${productLineKey.toLowerCase().trim()}${AGGREGATED_SKU_ID_SEPARATOR}${String(skuName ?? '').toLowerCase().trim()}`
}

/** Row id for an aggregated SKU. `updateAggregatedSKUData` parses this back into (product line, SKU name). */
export function buildAggregatedSKUId(productLineKey: string, skuName: string): string {
  return `sku-${aggregatedSKUKey(productLineKey, skuName)}`
}

/** Inverse of `buildAggregatedSKUId`; null when the id is not a well-formed aggregated SKU id. */
function parseAggregatedSKUId(id: string): { productLineKey: string, skuName: string } | null {
  if (!id.startsWith('sku-')) {
    return null
  }
  const parts = id.slice('sku-'.length).split(AGGREGATED_SKU_ID_SEPARATOR)
  if (parts.length !== 2 || parts[1] === '') {
    return null
  }
  return { productLineKey: parts[0], skuName: parts[1] }
}

/**
 * Aggregates all sales data by product line with SKU children
 * This creates a hierarchical structure where product lines can be expanded to show SKUs
 */
export function aggregateByProductLineWithSKUs(nodes: any[]): any[] {
  const productLineMap = new Map<string, any>()
  const skuMap = new Map<string, any>() // Track SKUs by product line
  
  function processNode(node: any, parentProductLine?: string) {
    // If this is a product line, aggregate its data
    if (node.level === HierarchyLevel.PRODUCT_LINE) {
      const productLineName = node.name
      const productLineKey = productLineName.toLowerCase().trim() // Use name as key for grouping
      
      if (productLineMap.has(productLineKey)) {
        // Merge with existing product line data
        const existing = productLineMap.get(productLineKey)!
        existing.sales_data = mergeSalesData(existing.sales_data, node.sales_data)
        // Use the most recent case cost and forecast config
        existing.case_cost = node.case_cost || existing.case_cost
        existing.forecast_config = node.forecast_config || existing.forecast_config
        existing.metadata = existing.metadata || node.metadata
        // Track all source IDs for reference
        if (!existing.source_ids) {
          existing.source_ids = [existing.id]
        }
        existing.source_ids.push(node.id)
      } else {
        // Create new product line entry
        productLineMap.set(productLineKey, {
          id: `aggregated-${productLineKey}`, // Use aggregated ID
          name: productLineName,
          level: HierarchyLevel.PRODUCT_LINE,
          depth: 0, // All product lines at same level
          parent_id: undefined,
          children: [],
          sales_data: [...node.sales_data],
          case_cost: node.case_cost,
          forecast_config: node.forecast_config,
          metadata: node.metadata,
          is_expanded: false,
          is_visible: true,
          has_children: false,
          source_ids: [node.id] // Track original source IDs
        })
      }
      
      // Process children of this product line
      if (node.children) {
        node.children.forEach((child: any) => processNode(child, productLineKey))
      }
    }
    
    // If this is a SKU, aggregate it under its product line
    if (node.level === HierarchyLevel.SKU && parentProductLine) {
      const skuKey = aggregatedSKUKey(parentProductLine, node.name)
      
      if (skuMap.has(skuKey)) {
        // Merge with existing SKU data
        const existing = skuMap.get(skuKey)!
        existing.sales_data = mergeSalesData(existing.sales_data, node.sales_data)
        existing.case_cost = node.case_cost || existing.case_cost
        existing.forecast_config = node.forecast_config || existing.forecast_config
        if (!existing.source_ids) {
          existing.source_ids = [existing.id]
        }
        existing.source_ids.push(node.id)
      } else {
        // Create new SKU entry
        skuMap.set(skuKey, {
          id: `sku-${skuKey}`,
          name: node.name,
          level: HierarchyLevel.SKU,
          depth: 1, // SKUs are one level below product lines
          parent_id: `aggregated-${parentProductLine}`,
          children: [],
          sales_data: [...node.sales_data],
          case_cost: node.case_cost,
          forecast_config: node.forecast_config,
          is_expanded: false,
          is_visible: true,
          has_children: false,
          source_ids: [node.id],
          product_sku: (node as any).product_sku,
          sku_name: (node as any).sku_name
        })
      }
    }
    
    // Process children
    if (node.children) {
      node.children.forEach((child: any) => processNode(child, parentProductLine))
    }
    
    // Process retailer relationships
    if (node.retailer_relationships) {
      node.retailer_relationships.forEach((relationship: any) => {
        if (relationship.children) {
          relationship.children.forEach((child: any) => processNode(child, parentProductLine))
        }
      })
    }
    
    // Process DC relationships
    if (node.dc_relationships) {
      node.dc_relationships.forEach((relationship: any) => {
        if (relationship.children) {
          relationship.children.forEach((child: any) => processNode(child, parentProductLine))
        }
      })
    }
  }
  
  // Process all nodes
  nodes.forEach((node: any) => processNode(node))
  
  function totalVolume(node: any): number {
    return (node.sales_data || []).reduce((sum: number, dataPoint: any) => {
      return sum + Number(dataPoint.metrics?.volume_dollars || dataPoint.volume_dollars || 0)
    }, 0)
  }

  // Add SKUs as children to their product lines
  const productLines = Array.from(productLineMap.values())
  productLines.forEach(productLine => {
    const productLineKey = productLine.name.toLowerCase().trim()
    const skus = Array.from(skuMap.values()).filter(sku => 
      sku.parent_id === `aggregated-${productLineKey}`
    )
    
    if (skus.length > 0) {
      productLine.children = skus.sort((a, b) => totalVolume(b) - totalVolume(a))
      productLine.has_children = true
    }
  })
  
  return productLines.sort((a, b) => totalVolume(b) - totalVolume(a))
}

/**
 * Updates data for aggregated SKUs in the original hierarchy
 * This function maps aggregated SKU IDs back to their original source nodes
 *
 * The aggregated cell shows the SUM of every source SKU, so a metric edit is split
 * proportionally across those sources (same rule as `updateAggregatedProductLineData`) —
 * writing the typed value onto each source would multiply the row by its source count.
 * `case_cost` is not a sum, so it is applied verbatim to every source, and so is
 * `store_count`: the aggregated cell displays the MAX of its sources (`max_store_count`),
 * so a proportional split would redisplay a different number than the user typed.
 *
 * Sources are matched on (product line, SKU name), the same pair
 * `aggregateByProductLineWithSKUs` groups by: matching on name alone also edited SKUs
 * that happen to share a name under a different product line.
 */
export function updateAggregatedSKUData(
  originalNodes: any[],
  aggregatedSKUId: string,
  periodKey: string,
  field: EditableMetricField,
  value: number,
  column_config?: ColumnConfig
): any[] {
  const parsed = parseAggregatedSKUId(aggregatedSKUId)
  if (!parsed) {
    return originalNodes
  }
  const { productLineKey, skuName } = parsed

  /** A source belongs to this aggregate only when its product line AND its own name match. */
  function isMatchingSKU(node: any, parentProductLine?: string): boolean {
    return node.level === HierarchyLevel.SKU &&
      parentProductLine === productLineKey &&
      String(node.name ?? '').toLowerCase().trim() === skuName
  }

  /** Key that `children` inherit: a product line names its own children, anything else passes its parent's key through. */
  function childProductLineKey(node: any, parentProductLine?: string): string | undefined {
    return node.level === HierarchyLevel.PRODUCT_LINE
      ? String(node.name ?? '').toLowerCase().trim()
      : parentProductLine
  }

  /** The data points this edit targets on one source SKU (the cell's primary rows for the period). */
  function editableDataPoints(node: any): any[] {
    const primary_rows = new Set(select_primary_sales_rows(node.sales_data))
    return (node.sales_data || []).filter(
      (dataPoint: any) => dataPoint.period?.period_key === periodKey && primary_rows.has(dataPoint)
    )
  }

  // Pass 1: total the current values so the edit can be distributed by each source's share.
  let totalCurrentValue = 0
  let matchedPointCount = 0
  function collectCurrentTotal(nodes: any[], parentProductLine?: string): void {
    for (const node of nodes) {
      if (isMatchingSKU(node, parentProductLine)) {
        for (const dataPoint of editableDataPoints(node)) {
          totalCurrentValue += Number(dataPoint.metrics?.[field] || 0)
          matchedPointCount += 1
        }
      }

      const childKey = childProductLineKey(node, parentProductLine)
      if (node.children) {
        collectCurrentTotal(node.children, childKey)
      }
      // Relationship children inherit the incoming key, mirroring aggregateByProductLineWithSKUs.
      for (const relationship of node.retailer_relationships || []) {
        if (relationship.children) {
          collectCurrentTotal(relationship.children, parentProductLine)
        }
      }
      for (const relationship of node.dc_relationships || []) {
        if (relationship.children) {
          collectCurrentTotal(relationship.children, parentProductLine)
        }
      }
    }
  }
  if (field !== 'case_cost' && field !== 'store_count') {
    collectCurrentTotal(originalNodes)
  }

  // Find all SKU nodes with matching name and parent product line
  function findAndUpdateSKUs(nodes: any[], parentProductLine?: string): any[] {
    return nodes.map(node => {
      const updatedNode = { ...node }

      // Check if this is a SKU under the addressed product line
      if (isMatchingSKU(node, parentProductLine)) {

        // Update the sales data for this SKU
        if (field === 'case_cost') {
          updatedNode.case_cost = value

          // Also update the forecast_config if it exists
          if (updatedNode.forecast_config) {
            updatedNode.forecast_config = {
              ...updatedNode.forecast_config,
              cost_per_case: value
            }
          }
        } else {
          const sku_cost = Number(node.case_cost || node.forecast_config?.cost_per_case || 0)
          const primary_rows = new Set(select_primary_sales_rows(node.sales_data))
          updatedNode.sales_data = (node.sales_data || []).map((dataPoint: any) => {
            if (dataPoint.period?.period_key === periodKey && primary_rows.has(dataPoint)) {
              if (field === 'store_count') {
                return apply_metric_edit_to_data_point(dataPoint, field, value, sku_cost, column_config)
              }
              const currentValue = Number(dataPoint.metrics?.[field] || 0)
              const proportion = totalCurrentValue > 0
                ? currentValue / totalCurrentValue
                : 1 / matchedPointCount
              return apply_metric_edit_to_data_point(dataPoint, field, value * proportion, sku_cost, column_config)
            }
            return dataPoint
          })
        }
      }

      const childProductLine = childProductLineKey(node, parentProductLine)

      // Recursively update children
      if (node.children) {
        updatedNode.children = findAndUpdateSKUs(node.children, childProductLine)
      }

      // Update retailer relationships
      if (node.retailer_relationships) {
        updatedNode.retailer_relationships = node.retailer_relationships.map((relationship: any) => {
          const updatedRelationship = { ...relationship }

          if (relationship.children) {
            updatedRelationship.children = findAndUpdateSKUs(relationship.children, parentProductLine)
          }

          return updatedRelationship
        })
      }

      // Update DC relationships
      if (node.dc_relationships) {
        updatedNode.dc_relationships = node.dc_relationships.map((relationship: any) => {
          const updatedRelationship = { ...relationship }

          if (relationship.children) {
            updatedRelationship.children = findAndUpdateSKUs(relationship.children, parentProductLine)
          }

          return updatedRelationship
        })
      }

      return updatedNode
    })
  }

  return findAndUpdateSKUs(originalNodes)
}

/**
 * Updates data in the original hierarchy for aggregated product lines
 * This function finds all source nodes for an aggregated product line and updates them
 */
export function updateAggregatedProductLineData(
  originalNodes: any[],
  aggregatedProductLineId: string,
  periodKey: string,
  field: EditableMetricField,
  value: number,
  column_config?: ColumnConfig
): any[] {
  // Find the aggregated product line to get its source IDs
  const aggregatedData = aggregateByProductLine(originalNodes)
  const aggregatedProductLine = aggregatedData.find(node => node.id === aggregatedProductLineId)
  
  if (!aggregatedProductLine || !aggregatedProductLine.source_ids) {
    return originalNodes
  }
  
  // Calculate the total current value across all sources
  let totalCurrentValue = 0
  const sourceIds = aggregatedProductLine.source_ids
  
  // Find all source nodes and calculate current total
  function findAndCalculateTotal(nodes: any[]): number {
    let total = 0
    for (const node of nodes) {
      if (node.level === HierarchyLevel.PRODUCT_LINE && sourceIds.includes(node.id)) {
        if (field === 'case_cost') {
          total += node.case_cost || 0
        } else if (node.sales_data) {
          // Same primary-row rule as `updateAggregatedSKUData`: shadowed rows
          // (e.g. volume_based_estimate) must not count toward the split basis.
          const primary_rows = select_primary_sales_rows(node.sales_data)
          const dataPoint = primary_rows.find((dp: any) => dp.period.period_key === periodKey)
          if (dataPoint) {
            total += dataPoint.metrics[field] || 0
          }
        }
      }
      
      if (node.children) {
        total += findAndCalculateTotal(node.children)
      }
      if (node.retailer_relationships) {
        for (const rel of node.retailer_relationships) {
          if (rel.children) {
            total += findAndCalculateTotal(rel.children)
          }
        }
      }
      if (node.dc_relationships) {
        for (const rel of node.dc_relationships) {
          if (rel.children) {
            total += findAndCalculateTotal(rel.children)
          }
        }
      }
    }
    return total
  }
  
  totalCurrentValue = findAndCalculateTotal(originalNodes)
  
  // Distribute the new value proportionally across all sources
  function updateNodesProportionally(nodes: any[]): any[] {
    return nodes.map(node => {
      if (node.level === HierarchyLevel.PRODUCT_LINE && sourceIds.includes(node.id)) {
        const updatedNode = { ...node }
        
        if (field === 'case_cost') {
          // Same absolute case cost on every source row for this product line name (not a volume split).
          updatedNode.case_cost = value
          
          // Also update the forecast_config if it exists
          if (updatedNode.forecast_config) {
            updatedNode.forecast_config = {
              ...updatedNode.forecast_config,
              cost_per_case: value
            }
          }
        } else if (node.sales_data) {
          const line_cost = Number(node.case_cost || node.forecast_config?.cost_per_case || 0)
          const primary_rows = new Set(select_primary_sales_rows(node.sales_data))
          updatedNode.sales_data = node.sales_data.map((dataPoint: any) => {
            if (dataPoint.period.period_key === periodKey && primary_rows.has(dataPoint)) {
              if (field === 'store_count') {
                // Aggregate displays max(sources) — write the typed count verbatim (see SKU variant).
                return apply_metric_edit_to_data_point(dataPoint, field, value, line_cost, column_config)
              }
              const currentValue = dataPoint.metrics[field] || 0
              const proportion = totalCurrentValue > 0 ? currentValue / totalCurrentValue : 1 / sourceIds.length
              return apply_metric_edit_to_data_point(dataPoint, field, value * proportion, line_cost, column_config)
            }
            return dataPoint
          })
        }
        
        return updatedNode
      }
      
      // Recursively update children
      const updatedNode = { ...node }
      if (node.children) {
        updatedNode.children = updateNodesProportionally(node.children)
      }
      if (node.retailer_relationships) {
        updatedNode.retailer_relationships = node.retailer_relationships.map((rel: any) => ({
          ...rel,
          children: rel.children ? updateNodesProportionally(rel.children) : rel.children
        }))
      }
      if (node.dc_relationships) {
        updatedNode.dc_relationships = node.dc_relationships.map((rel: any) => ({
          ...rel,
          children: rel.children ? updateNodesProportionally(rel.children) : rel.children
        }))
      }
      
      return updatedNode
    })
  }
  
  return updateNodesProportionally(originalNodes)
}

/**
 * Aggregates all sales data by class of trade across all hierarchy levels
 * This creates a flat list of class of trade with aggregated sales data
 * Classes of trade with the same name are combined into a single entry
 */
export function aggregateByClassOfTrade(nodes: any[]): any[] {
  const classOfTradeMap = new Map<string, any>()
  
  function processNode(node: any) {
    // If this is a class of trade, aggregate its data
    if (node.level === HierarchyLevel.CLASS_OF_TRADE) {
      const classOfTradeName = node.name
      const classOfTradeKey = classOfTradeName.toLowerCase().trim() // Use name as key for grouping
      
      if (classOfTradeMap.has(classOfTradeKey)) {
        // Merge with existing class of trade data
        const existing = classOfTradeMap.get(classOfTradeKey)!
        existing.sales_data = mergeSalesData(existing.sales_data, node.sales_data)
        // Track all source IDs for reference
        if (!existing.source_ids) {
          existing.source_ids = [existing.id]
        }
        existing.source_ids.push(node.id)
      } else {
        // Create new class of trade entry
        classOfTradeMap.set(classOfTradeKey, {
          id: `aggregated-${classOfTradeKey}`, // Use aggregated ID
          name: classOfTradeName,
          level: HierarchyLevel.CLASS_OF_TRADE,
          depth: 0, // All class of trade at same level
          parent_id: undefined,
          children: [],
          sales_data: [...node.sales_data],
          is_expanded: false,
          is_visible: true,
          has_children: false,
          source_ids: [node.id] // Track original source IDs
        })
      }
    }
    
    // Process children
    if (node.children) {
      node.children.forEach((child: any) => processNode(child))
    }
    
    // Process retailer relationships
    if (node.retailer_relationships) {
      node.retailer_relationships.forEach((relationship: any) => {
        if (relationship.children) {
          relationship.children.forEach((child: any) => processNode(child))
        }
      })
    }
    
    // Process DC relationships
    if (node.dc_relationships) {
      node.dc_relationships.forEach((relationship: any) => {
        if (relationship.children) {
          relationship.children.forEach((child: any) => processNode(child))
        }
      })
    }
  }
  
  // Process all nodes
  nodes.forEach(node => processNode(node))
  
  // Convert map to array and sort by name
  return Array.from(classOfTradeMap.values()).sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Aggregates classes of trade with the same name in the hierarchy
 * This modifies the hierarchy in place to merge duplicate class of trade nodes
 */
export function aggregateDuplicateClassOfTrade(nodes: any[]): any[] {
  const classOfTradeMap = new Map<string, any>()
  const processedNodes: any[] = []
  
  function processNode(node: any): any {
    if (node.level === HierarchyLevel.CLASS_OF_TRADE) {
      const classOfTradeName = node.name
      const classOfTradeKey = classOfTradeName.toLowerCase().trim()
      
      if (classOfTradeMap.has(classOfTradeKey)) {
        // Merge with existing class of trade
        const existing = classOfTradeMap.get(classOfTradeKey)!
        
        // Merge children
        if (node.children) {
          existing.children = [...(existing.children || []), ...node.children]
        }
        
        // Merge retailer relationships
        if (node.retailer_relationships) {
          existing.retailer_relationships = [
            ...(existing.retailer_relationships || []), 
            ...node.retailer_relationships
          ]
        }
        
        // Merge DC relationships
        if (node.dc_relationships) {
          existing.dc_relationships = [
            ...(existing.dc_relationships || []), 
            ...node.dc_relationships
          ]
        }
        
        // Merge sales data
        existing.sales_data = mergeSalesData(existing.sales_data || [], node.sales_data || [])
        
        return null // Don't add this node, it's been merged
      } else {
        // First occurrence of this class of trade
        const aggregatedNode = {
          ...node,
          children: node.children || [],
          retailer_relationships: node.retailer_relationships || [],
          dc_relationships: node.dc_relationships || [],
          sales_data: node.sales_data || []
        }
        classOfTradeMap.set(classOfTradeKey, aggregatedNode)
        return aggregatedNode
      }
    } else {
      // Process children for other node types
      const processedNode = { ...node }
      
      if (node.children) {
        processedNode.children = node.children
          .map((child: any) => processNode(child))
          .filter((child: any) => child !== null)
      }
      
      if (node.retailer_relationships) {
        processedNode.retailer_relationships = node.retailer_relationships.map((rel: any) => ({
          ...rel,
          children: rel.children ? rel.children
            .map((child: any) => processNode(child))
            .filter((child: any) => child !== null) : rel.children
        }))
      }
      
      if (node.dc_relationships) {
        processedNode.dc_relationships = node.dc_relationships.map((rel: any) => ({
          ...rel,
          children: rel.children ? rel.children
            .map((child: any) => processNode(child))
            .filter((child: any) => child !== null) : rel.children
        }))
      }
      
      return processedNode
    }
  }
  
  // Process all nodes
  nodes.forEach(node => {
    const processedNode = processNode(node)
    if (processedNode !== null) {
      processedNodes.push(processedNode)
    }
  })
  
  return processedNodes
}

/** Merge two period data points when rolling sales_data up from siblings or branches (same semantics as mergeSalesData). */
export function rollupMergeSalesDataPoints(existing: any, dataPoint: any): any {
  const mergedDetail = merge_detail_metrics_sum(existing.metrics?.detail, dataPoint.metrics?.detail)
  // Neither side reporting units means unknown, not zero — see `add_sales_metrics_into_target`.
  const unitSum = sum_optional_units(existing.metrics?.units_sold, dataPoint.metrics?.units_sold)
  const aggregatedMetrics: SalesMetrics = {
    store_count: max_store_count(existing.metrics.store_count, dataPoint.metrics.store_count),
    // Distribution points sum (each side falls back to its door count when it is a leaf).
    sku_store_count:
      Number(existing.metrics.sku_store_count ?? existing.metrics.store_count ?? 0) +
      Number(dataPoint.metrics.sku_store_count ?? dataPoint.metrics.store_count ?? 0),
    volume_dollars: existing.metrics.volume_dollars + dataPoint.metrics.volume_dollars,
    cases_sold: existing.metrics.cases_sold + dataPoint.metrics.cases_sold,
    part_cases: metrics_part_cases(existing.metrics) + metrics_part_cases(dataPoint.metrics),
    case_cost: dataPoint.metrics.case_cost ?? existing.metrics.case_cost ?? 0,
    units_sold: unitSum,
    ...(mergedDetail ? { detail: mergedDetail } : {}),
  }

  const aggregatedForecastMetrics =
    existing.forecast_metrics && dataPoint.forecast_metrics
      ? {
          store_count: max_store_count(existing.forecast_metrics.store_count, dataPoint.forecast_metrics.store_count),
          volume_dollars: existing.forecast_metrics.volume_dollars + dataPoint.forecast_metrics.volume_dollars,
          cases_sold: existing.forecast_metrics.cases_sold + dataPoint.forecast_metrics.cases_sold,
          case_cost: dataPoint.forecast_metrics.case_cost ?? existing.forecast_metrics.case_cost ?? 0,
          units_sold: sum_optional_units(
            existing.forecast_metrics.units_sold,
            dataPoint.forecast_metrics.units_sold,
          ),
          ...(() => {
            const d = merge_detail_metrics_sum(
              existing.forecast_metrics.detail,
              dataPoint.forecast_metrics.detail,
            )
            return d ? { detail: d } : {}
          })(),
        }
      : dataPoint.forecast_metrics || existing.forecast_metrics

  const aggregatedActualMetrics =
    existing.actual_metrics && dataPoint.actual_metrics
      ? {
          store_count: max_store_count(existing.actual_metrics.store_count, dataPoint.actual_metrics.store_count),
          volume_dollars: existing.actual_metrics.volume_dollars + dataPoint.actual_metrics.volume_dollars,
          cases_sold: existing.actual_metrics.cases_sold + dataPoint.actual_metrics.cases_sold,
          case_cost: dataPoint.actual_metrics.case_cost ?? existing.actual_metrics.case_cost ?? 0,
          units_sold: sum_optional_units(
            existing.actual_metrics.units_sold,
            dataPoint.actual_metrics.units_sold,
          ),
          ...(() => {
            const d = merge_detail_metrics_sum(
              existing.actual_metrics.detail,
              dataPoint.actual_metrics.detail,
            )
            return d ? { detail: d } : {}
          })(),
        }
      : dataPoint.actual_metrics || existing.actual_metrics

  let recalculatedVariance = null
  if (aggregatedForecastMetrics && aggregatedActualMetrics) {
    recalculatedVariance = {
      store_count_variance: aggregatedActualMetrics.store_count - aggregatedForecastMetrics.store_count,
      volume_dollars_variance: aggregatedActualMetrics.volume_dollars - aggregatedForecastMetrics.volume_dollars,
      cases_sold_variance: aggregatedActualMetrics.cases_sold - aggregatedForecastMetrics.cases_sold,
      store_count_variance_pct:
        aggregatedForecastMetrics.store_count > 0
          ? ((aggregatedActualMetrics.store_count - aggregatedForecastMetrics.store_count) /
              aggregatedForecastMetrics.store_count) *
            100
          : 0,
      volume_dollars_variance_pct:
        aggregatedForecastMetrics.volume_dollars > 0
          ? ((aggregatedActualMetrics.volume_dollars - aggregatedForecastMetrics.volume_dollars) /
              aggregatedForecastMetrics.volume_dollars) *
            100
          : 0,
      cases_sold_variance_pct:
        aggregatedForecastMetrics.cases_sold > 0
          ? ((aggregatedActualMetrics.cases_sold - aggregatedForecastMetrics.cases_sold) /
              aggregatedForecastMetrics.cases_sold) *
            100
          : 0,
    }
  }

  const merged_compare_axis =
    existing.compare_axis &&
    dataPoint.compare_axis &&
    existing.compare_axis === dataPoint.compare_axis
      ? existing.compare_axis
      : recalculatedVariance
        ? "dollars"
        : existing.compare_axis || dataPoint.compare_axis

  return {
    ...existing,
    metrics: aggregatedMetrics,
    is_forecast: dataPoint.is_forecast,
    is_actual: dataPoint.is_actual,
    forecast_metrics: aggregatedForecastMetrics,
    actual_metrics: aggregatedActualMetrics,
    variance: recalculatedVariance || dataPoint.variance || existing.variance,
    compare_axis: merged_compare_axis,
  }
}

/**
 * Merges sales data from multiple sources for the same product line
 */
function mergeSalesData(existingData: any[], newData: any[]): any[] {
  const mergedData = new Map<string, any>()
  
  // Add existing data
  existingData.forEach(dataPoint => {
    const periodKey = dataPoint.period.period_key
    mergedData.set(periodKey, { ...dataPoint })
  })
  
  // Merge new data
  newData.forEach(dataPoint => {
    const periodKey = dataPoint.period.period_key
    
    if (mergedData.has(periodKey)) {
      const existing = mergedData.get(periodKey)!
      mergedData.set(periodKey, rollupMergeSalesDataPoints(existing, dataPoint))
    } else {
      // Add new data point
      mergedData.set(periodKey, { ...dataPoint })
    }
  })
  
  return Array.from(mergedData.values()).sort((a, b) => 
    a.period.year - b.period.year || a.period.month - b.period.month
  )
}

/** True when the planned-volume formula must not touch this row's dollars. */
function is_volume_recalc_exempt(dataPoint: any): boolean {
  if (dataPoint.is_locked) return true
  const kind = dataPoint.source_kind
  if (kind === 'actual' || dataPoint.is_actual === true) return true
  // Imported / estimated / manually overridden rows carry real revenue whose
  // pricing may not follow stores × cases × cost; leave their dollars alone.
  if (kind && kind !== 'forecast' && kind !== 'opportunity') return true
  return false
}

/**
 * Recalculates volume for plan rows in the hierarchy
 * Volume = stores × cases × cost_per_case
 * Actual, locked, and imported rows keep their stored dollars.
 */
export function recalculateVolumeForAllRows(nodes: any[]): any[] {
  function recalculateNode(node: any): any {
    const updatedNode = { ...node }

    // Recalculate volume for this node's sales data.
    // Only applies to rows with an explicit case_cost — rows with pure revenue (caseCost = 0)
    // are left unchanged so their volume_dollars is preserved through the aggregation pass.
    const caseCost = node.case_cost || (node.forecast_config?.cost_per_case) || 0
    if (node.sales_data && node.sales_data.length > 0 && caseCost > 0) {
      const opp = node.metadata?.opportunity
      updatedNode.sales_data = node.sales_data.map((dataPoint: any) => {
        if (is_volume_recalc_exempt(dataPoint)) {
          return dataPoint
        }
        if (opp && opp.status !== "live") {
          // After updateOpportunityLikelihood, metrics may already be likelihood-scaled.
          // Always derive volume from opportunity_base_metrics (the unscaled truth) so we
          // don't corrupt the base or double-scale store/case counts on the next display pass.
          const baseMetrics = dataPoint.opportunity_base_metrics || dataPoint.metrics
          const baseVolume = baseMetrics.store_count * baseMetrics.cases_sold * caseCost
          return {
            ...dataPoint,
            metrics: {
              ...dataPoint.metrics,
              case_cost: caseCost,
              volume_dollars: baseVolume,
            },
            opportunity_base_metrics: {
              ...baseMetrics,
              volume_dollars: baseVolume,
              case_cost: caseCost,
            },
          }
        }
        const newVolume = dataPoint.metrics.store_count * dataPoint.metrics.cases_sold * caseCost
        return {
          ...dataPoint,
          metrics: {
            ...dataPoint.metrics,
            case_cost: caseCost,
            volume_dollars: newVolume,
          },
        }
      })
    }
    
    // Recursively process children
    if (node.children) {
      updatedNode.children = node.children.map((child: any) => recalculateNode(child))
    }
    
    // Process retailer relationships
    if (node.retailer_relationships) {
      updatedNode.retailer_relationships = node.retailer_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }
        
        // Only recalculate volume when the relationship has a case_cost set.
        const relCaseCost = relationship.case_cost || (relationship.forecast_config?.cost_per_case) || 0
        if (relationship.sales_data && relationship.sales_data.length > 0 && relCaseCost > 0) {
          updatedRelationship.sales_data = relationship.sales_data.map((dataPoint: any) =>
            is_volume_recalc_exempt(dataPoint)
              ? dataPoint
              : {
                  ...dataPoint,
                  metrics: {
                    ...dataPoint.metrics,
                    case_cost: relCaseCost,
                    volume_dollars: dataPoint.metrics.store_count * dataPoint.metrics.cases_sold * relCaseCost,
                  },
                }
          )
        }

        // Process relationship children (product lines)
        if (relationship.children) {
          updatedRelationship.children = relationship.children.map((child: any) => recalculateNode(child))
        }
        
        return updatedRelationship
      })
    }
    
    // Process DC relationships
    if (node.dc_relationships) {
      updatedNode.dc_relationships = node.dc_relationships.map((relationship: any) => {
        const updatedRelationship = { ...relationship }
        
        const dcCaseCost = relationship.case_cost || (relationship.forecast_config?.cost_per_case) || 0
        if (relationship.sales_data && relationship.sales_data.length > 0 && dcCaseCost > 0) {
          updatedRelationship.sales_data = relationship.sales_data.map((dataPoint: any) =>
            is_volume_recalc_exempt(dataPoint)
              ? dataPoint
              : {
                  ...dataPoint,
                  metrics: {
                    ...dataPoint.metrics,
                    case_cost: dcCaseCost,
                    volume_dollars: dataPoint.metrics.store_count * dataPoint.metrics.cases_sold * dcCaseCost,
                  },
                }
          )
        }

        return updatedRelationship
      })
    }

    return updatedNode
  }
  
  return nodes.map(node => recalculateNode(node))
}

/** Every node id in the hierarchy forest (depth-first). */
export function collectAllHierarchyNodeIds(nodes: any[]): Set<string> {
  const ids = new Set<string>()
  function walk(n: any): void {
    if (!n?.id) {
      return
    }
    ids.add(n.id)
    if (Array.isArray(n.children)) {
      for (const c of n.children) {
        walk(c)
      }
    }
  }
  for (const root of nodes) {
    walk(root)
  }
  return ids
}

/**
 * Removes the subtree rooted at the first node with `id === nodeId` (including that node).
 * Does not modify relationship slices (`retailer_relationships` / `dc_relationships`).
 */
export function removeHierarchySubtreeById(nodes: any[], nodeId: string): any[] {
  function transform(n: any): any | null {
    if (n.id === nodeId) {
      return null
    }
    const children = Array.isArray(n.children)
      ? n.children.map((ch: any) => transform(ch)).filter((ch: any) => ch !== null)
      : n.children
    return {
      ...n,
      children,
    }
  }
  return nodes.map((n: any) => transform(n)).filter((n: any) => n !== null)
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("store count roll-up", () => {
    function point(period_key: string, store_count: number, volume_dollars: number, cases_sold: number): any {
      return {
        period: { year: 2027, month: Number(period_key.slice(5)), period_key },
        metrics: { store_count, volume_dollars, cases_sold, case_cost: 1 },
      }
    }

    it("takes the widest child rather than the sum", () => {
      expect(max_store_count(255, 255)).toBe(255)
      expect(max_store_count(0, 191)).toBe(191)
      expect(max_store_count(120, 40)).toBe(120)
      expect(max_store_count(null, undefined)).toBe(0)
    })

    it("rolls four co-located SKUs up to one door count, not four", () => {
      // The Kroger shape: an 850-store listing at 30% likelihood on each of 4 SKUs.
      const customer = {
        id: "kroger",
        name: "Kroger",
        level: HierarchyLevel.CUSTOMER,
        sales_data: [],
        children: ["a", "b", "c", "d"].map((sku: string) => ({
          id: sku,
          name: sku,
          level: HierarchyLevel.SKU,
          sales_data: [point("2027-04", 255, 1000, 100)],
        })),
      } as any

      const rolled = aggregateChildrenToParents([customer])
      const april = rolled[0].sales_data.find((dp: any) => dp.period.period_key === "2027-04")

      expect(april.metrics.store_count).toBe(255)
      // Volume and cases are additive — only distribution is not.
      expect(april.metrics.volume_dollars).toBe(4000)
      expect(april.metrics.cases_sold).toBe(400)
    })

    it("reports the widest branch when children reach different door counts", () => {
      const customer = {
        id: "c",
        name: "Customer",
        level: HierarchyLevel.CUSTOMER,
        sales_data: [],
        children: [
          { id: "wide", name: "wide", level: HierarchyLevel.SKU, sales_data: [point("2027-04", 900, 10, 1)] },
          { id: "narrow", name: "narrow", level: HierarchyLevel.SKU, sales_data: [point("2027-04", 50, 10, 1)] },
        ],
      } as any

      const rolled = aggregateChildrenToParents([customer])
      const april = rolled[0].sales_data.find((dp: any) => dp.period.period_key === "2027-04")
      expect(april.metrics.store_count).toBe(900)
    })
  })

  describe("removeHierarchySubtreeById", () => {
    it("removes a nested child and keeps siblings", () => {
      const tree = [{ id: "a", children: [{ id: "b" }, { id: "c" }] }]
      const out = removeHierarchySubtreeById(tree, "b")
      expect(out[0].children).toHaveLength(1)
      expect(out[0].children[0].id).toBe("c")
    })

    it("removes a root node", () => {
      const tree = [{ id: "a" }, { id: "b" }]
      const out = removeHierarchySubtreeById(tree, "a")
      expect(out).toHaveLength(1)
      expect(out[0].id).toBe("b")
    })
  })

  describe("collectAllHierarchyNodeIds", () => {
    it("collects nested ids", () => {
      const tree = [{ id: "r", children: [{ id: "c" }] }]
      expect([...collectAllHierarchyNodeIds(tree)].sort()).toEqual(["c", "r"])
    })
  })

  describe("updateNodeNameInHierarchy", () => {
    it("renames a nested node and sku_name for SKU level", () => {
      const tree = [
        {
          id: "p",
          name: "P",
          level: HierarchyLevel.PRODUCT_LINE,
          sales_data: [],
          children: [
            {
              id: "s",
              name: "Old SKU",
              level: HierarchyLevel.SKU,
              sku_name: "Old SKU",
              sales_data: [],
            },
          ],
        },
      ]
      const out = updateNodeNameInHierarchy(tree, "s", "  Fresh name  ")
      expect((out[0] as any).children[0].name).toBe("Fresh name")
      expect((out[0] as any).children[0].sku_name).toBe("Fresh name")
    })
  })

  describe("calculateNextForecastMetrics", () => {
    it("applies growth multipliers as rates", () => {
      expect(calculateNextForecastMetrics(
        { store_count: 100, cases_sold: 200, volume_dollars: 5000 },
        { store_count_growth_multiplier: 0.02, same_store_sales_multiplier: 0.03 }
      )).toEqual({
        store_count: 102,
        cases_sold: 206,
        volume_dollars: 5150
      })
    })

    it("allows explicit zero growth multipliers for imported forecasts", () => {
      expect(calculateNextForecastMetrics(
        { store_count: 100, cases_sold: 200, volume_dollars: 5000 },
        { store_count_growth_multiplier: 0, same_store_sales_multiplier: 0 }
      )).toEqual({
        store_count: 100,
        cases_sold: 200,
        volume_dollars: 5000
      })
    })
  })

  describe("aggregateChildrenToParents (retailer relationship rollups)", () => {
    it("keeps summed child PL volume when relationship.case_cost is zero or stale", () => {
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      const dp = (vol: number, s: number, c: number) => ({
        period,
        metrics: { store_count: s, cases_sold: c, volume_dollars: vol, case_cost: 0 },
        is_forecast: true,
        source_kind: "forecast",
      })
      const forest = [
        {
          id: "dc",
          level: HierarchyLevel.DISTRIBUTION_CENTER,
          sales_data: [],
          retailer_relationships: [
            {
              retailer_id: "r1",
              retailer_name: "R1",
              case_cost: 0,
              children: [
                {
                  id: "pl1",
                  level: HierarchyLevel.PRODUCT_LINE,
                  name: "A",
                  case_cost: 10,
                  sales_data: [dp(500, 10, 5)],
                },
                {
                  id: "pl2",
                  level: HierarchyLevel.PRODUCT_LINE,
                  name: "A",
                  case_cost: 20,
                  sales_data: [dp(1000, 10, 5)],
                },
              ],
            },
          ],
        },
      ]
      const out = aggregateChildrenToParents(forest as any)
      const rel = out[0].retailer_relationships[0]
      const rolled = rel.sales_data.find((d: any) => d.period.period_key === "2026-01")
      // Volume and cases sum; doors do not — both product lines sit in the same 10 stores.
      expect(rolled.metrics.store_count).toBe(10)
      expect(rolled.metrics.cases_sold).toBe(10)
      expect(rolled.metrics.volume_dollars).toBe(1500)
    })

    it("sums units_sold and detail keys when rolling child PL rows onto the relationship", () => {
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      const dp = (units: number, detail?: Record<string, number>) => ({
        period,
        metrics: {
          store_count: 5,
          cases_sold: 1,
          volume_dollars: 100,
          case_cost: 0,
          units_sold: units,
          detail,
        },
        is_forecast: true,
        source_kind: "forecast",
      })
      const forest = [
        {
          id: "dc",
          level: HierarchyLevel.DISTRIBUTION_CENTER,
          sales_data: [],
          retailer_relationships: [
            {
              retailer_id: "r1",
              retailer_name: "R1",
              case_cost: 0,
              children: [
                {
                  id: "pl1",
                  level: HierarchyLevel.PRODUCT_LINE,
                  name: "A",
                  case_cost: 10,
                  sales_data: [dp(100, { forecasted_consumption_units: 100 })],
                },
                {
                  id: "pl2",
                  level: HierarchyLevel.PRODUCT_LINE,
                  name: "B",
                  case_cost: 10,
                  sales_data: [dp(200, { forecasted_consumption_units: 300 })],
                },
              ],
            },
          ],
        },
      ]
      const out = aggregateChildrenToParents(forest as any)
      const rel = out[0].retailer_relationships[0]
      const rolled = rel.sales_data.find((d: any) => d.period.period_key === "2026-01")
      expect(rolled.metrics.units_sold).toBe(300)
      expect(rolled.metrics.detail?.forecasted_consumption_units).toBe(400)
    })

    it("rolls up to no units at all when no child reports units (renders '—', not 0)", () => {
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      // A dollars-only month: revenue is known, unit volume is simply not in the source.
      const dp = (units?: number) => ({
        period,
        metrics: { store_count: 5, cases_sold: 0, volume_dollars: 100, case_cost: 0, units_sold: units },
        is_forecast: true,
        source_kind: "forecast",
      })
      const forest = (units_a?: number, units_b?: number) => [
        {
          id: "dc",
          level: HierarchyLevel.DISTRIBUTION_CENTER,
          sales_data: [],
          retailer_relationships: [
            {
              retailer_id: "r1",
              retailer_name: "R1",
              case_cost: 0,
              children: [
                { id: "pl1", level: HierarchyLevel.PRODUCT_LINE, name: "A", case_cost: 0, sales_data: [dp(units_a)] },
                { id: "pl2", level: HierarchyLevel.PRODUCT_LINE, name: "B", case_cost: 0, sales_data: [dp(units_b)] },
              ],
            },
          ],
        },
      ]

      const none = aggregateChildrenToParents(forest() as any)[0].retailer_relationships[0].sales_data[0]
      expect(none.metrics.units_sold ?? null).toBeNull()
      expect(none.metrics.volume_dollars).toBe(200)

      // Once any child reports units, the silent siblings count as zero.
      const partial = aggregateChildrenToParents(forest(undefined, 50) as any)[0].retailer_relationships[0].sales_data[0]
      expect(partial.metrics.units_sold).toBe(50)
    })

    it("keeps a customer row's units unknown when every SKU beneath it is dollars-only", () => {
      // The shape the Confido import produces: customer → product_line → sku, where the
      // 2026 actual months carry revenue but no unit volume.
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      const sku = (id: string, dollars: number, units?: number) => ({
        id,
        level: HierarchyLevel.PRODUCT_LINE,
        name: id,
        sales_data: [
          {
            period,
            metrics: { store_count: 0, cases_sold: 0, volume_dollars: dollars, case_cost: 0, units_sold: units },
            source_kind: "actual",
            is_actual: true,
          },
        ],
      })
      const customer = (children: any[]) => [
        { id: "cust", level: HierarchyLevel.RETAILER, name: "UNFI", sales_data: [], children },
      ]

      const dollars_only = aggregateChildrenToParents(customer([sku("a", 100), sku("b", 200)]) as any)
      expect(dollars_only[0].sales_data[0].metrics.volume_dollars).toBe(300)
      expect(dollars_only[0].sales_data[0].metrics.units_sold ?? null).toBeNull()

      const with_units = aggregateChildrenToParents(customer([sku("a", 100), sku("b", 200, 40)]) as any)
      expect(with_units[0].sales_data[0].metrics.units_sold).toBe(40)
    })
  })

  describe("updateAggregatedProductLineData", () => {
    it("applies the same case_cost to every source PL (no proportional split)", () => {
      const tree = [
        {
          id: "root",
          level: HierarchyLevel.CLASS_OF_TRADE,
          children: [
            {
              id: "pl1",
              level: HierarchyLevel.PRODUCT_LINE,
              name: "Berry Jam",
              case_cost: 24,
              sales_data: [],
            },
            {
              id: "pl2",
              level: HierarchyLevel.PRODUCT_LINE,
              name: "Berry Jam",
              case_cost: 48,
              sales_data: [],
            },
          ],
        },
      ]
      const next = updateAggregatedProductLineData(tree as any, "aggregated-berry jam", "", "case_cost", 40)
      const pl1 = (next[0] as any).children.find((n: any) => n.id === "pl1")
      const pl2 = (next[0] as any).children.find((n: any) => n.id === "pl2")
      expect(pl1.case_cost).toBe(40)
      expect(pl2.case_cost).toBe(40)
    })

    it("writes store_count verbatim to every source (aggregate displays the max, not a sum)", () => {
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      function pl(id: string, store_count: number) {
        return {
          id,
          level: HierarchyLevel.PRODUCT_LINE,
          name: "Berry Jam",
          case_cost: 10,
          sales_data: [
            { period, metrics: { store_count, cases_sold: 10, volume_dollars: 0, case_cost: 10 }, is_forecast: true, source_kind: "forecast" },
          ],
        }
      }
      const tree = [
        { id: "root", level: HierarchyLevel.CLASS_OF_TRADE, children: [pl("pl1", 100), pl("pl2", 100)] },
      ]
      const next = updateAggregatedProductLineData(tree as any, "aggregated-berry jam", "2026-01", "store_count", 500)
      const stores = (id: string) =>
        (next[0] as any).children.find((n: any) => n.id === id).sales_data[0].metrics.store_count
      // The aggregated cell displays max(sources); a proportional split of 500 across two
      // 100-door sources re-displayed as 250. Setting each source verbatim redisplays 500.
      expect(stores("pl1")).toBe(500)
      expect(stores("pl2")).toBe(500)
    })

    it("only edits the period's primary rows, never shadowed or actual rows", () => {
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      const tree = [
        {
          id: "root",
          level: HierarchyLevel.CLASS_OF_TRADE,
          children: [
            {
              id: "pl1",
              level: HierarchyLevel.PRODUCT_LINE,
              name: "Berry Jam",
              case_cost: 10,
              sales_data: [
                { period, metrics: { store_count: 0, cases_sold: 10, volume_dollars: 100, case_cost: 10 }, is_forecast: true, source_kind: "forecast" },
                { period, metrics: { store_count: 0, cases_sold: 8, volume_dollars: 80, case_cost: 10 }, is_forecast: true, source_kind: "volume_based_estimate" },
              ],
            },
          ],
        },
      ]
      const next = updateAggregatedProductLineData(tree as any, "aggregated-berry jam", "2026-01", "volume_dollars", 200)
      const rows = (next[0] as any).children[0].sales_data
      const forecast_row = rows.find((dp: any) => dp.source_kind === "forecast")
      const shadow_row = rows.find((dp: any) => dp.source_kind === "volume_based_estimate")
      expect(forecast_row.metrics.volume_dollars).toBe(200)
      // The shadowed VBE row must not absorb any part of the edit.
      expect(shadow_row.metrics.volume_dollars).toBe(80)
    })
  })

  describe("updateAggregatedSKUData", () => {
    const period = { year: 2026, month: 1, period_key: "2026-01" }

    function sku(id: string, name: string, volume_dollars: number, case_cost = 10) {
      return {
        id,
        level: HierarchyLevel.SKU,
        name,
        case_cost,
        sales_data: [
          {
            period,
            metrics: { store_count: 5, cases_sold: 10, volume_dollars, case_cost },
            is_forecast: true,
            source_kind: "forecast",
          },
        ],
      }
    }

    /** One customer branch holding a product line and its SKUs — SKUs aggregate across these branches. */
    function customer(id: string, productLineName: string, skus: any[]) {
      return {
        id: `${id}-customer`,
        level: HierarchyLevel.CUSTOMER,
        name: id,
        sales_data: [],
        children: [
          {
            id: `${id}-pl`,
            level: HierarchyLevel.PRODUCT_LINE,
            name: productLineName,
            case_cost: 10,
            sales_data: [],
            children: skus,
          },
        ],
      }
    }

    function findNode(nodes: any[], id: string): any {
      for (const node of nodes) {
        if (node.id === id) return node
        const found = node.children ? findNode(node.children, id) : undefined
        if (found) return found
      }
      return undefined
    }

    function volumeOf(nodes: any[], id: string): number {
      return findNode(nodes, id).sales_data[0].metrics.volume_dollars
    }

    it("splits a metric edit proportionally across the source SKUs instead of writing it to each", () => {
      const tree = [
        customer("c1", "Berry Jam", [sku("s1", "12oz", 100)]),
        customer("c2", "Berry Jam", [sku("s2", "12oz", 200)]),
        customer("c3", "Berry Jam", [sku("s3", "12oz", 300)]),
      ]
      // The aggregated cell shows 600; typing 300 must halve every source, not set each to 300.
      const next = updateAggregatedSKUData(
        tree as any,
        buildAggregatedSKUId("Berry Jam", "12oz"),
        "2026-01",
        "volume_dollars",
        300
      )
      expect(volumeOf(next, "s1")).toBe(50)
      expect(volumeOf(next, "s2")).toBe(100)
      expect(volumeOf(next, "s3")).toBe(150)
      expect(volumeOf(next, "s1") + volumeOf(next, "s2") + volumeOf(next, "s3")).toBe(300)
    })

    it("splits evenly when every source is currently zero", () => {
      const tree = [
        customer("c1", "Berry Jam", [sku("s1", "12oz", 0)]),
        customer("c2", "Berry Jam", [sku("s2", "12oz", 0)]),
      ]
      const next = updateAggregatedSKUData(
        tree as any,
        buildAggregatedSKUId("Berry Jam", "12oz"),
        "2026-01",
        "volume_dollars",
        400
      )
      expect(volumeOf(next, "s1")).toBe(200)
      expect(volumeOf(next, "s2")).toBe(200)
    })

    it("leaves same-named SKUs under a different product line alone", () => {
      const tree = [
        customer("c1", "Berry Jam", [sku("s1", "12oz", 100)]),
        customer("c2", "Peach Jam", [sku("s2", "12oz", 100)]),
      ]
      const next = updateAggregatedSKUData(
        tree as any,
        buildAggregatedSKUId("Berry Jam", "12oz"),
        "2026-01",
        "volume_dollars",
        500
      )
      expect(volumeOf(next, "s1")).toBe(500)
      expect(volumeOf(next, "s2")).toBe(100)
    })

    it("resolves product line names containing hyphens", () => {
      const tree = [customer("c1", "Berry-Jam", [sku("s1", "12oz", 100)])]
      const aggregated = aggregateByProductLineWithSKUs(tree as any)
      const aggregatedSKU = aggregated[0].children[0]
      // The id the grid hands back must round-trip: a '-' separator parsed this as
      // product line "berry" / SKU "jam-12oz" and the edit silently did nothing.
      expect(aggregatedSKU.id).toBe(buildAggregatedSKUId("Berry-Jam", "12oz"))

      const next = updateAggregatedSKUData(tree as any, aggregatedSKU.id, "2026-01", "volume_dollars", 250)
      expect(volumeOf(next, "s1")).toBe(250)
    })

    it("writes store_count verbatim to every source SKU (aggregate displays the max)", () => {
      const tree = [
        customer("c1", "Berry Jam", [sku("s1", "12oz", 0)]),
        customer("c2", "Berry Jam", [sku("s2", "12oz", 0)]),
      ]
      const next = updateAggregatedSKUData(
        tree as any,
        buildAggregatedSKUId("Berry Jam", "12oz"),
        "2026-01",
        "store_count",
        500
      )
      // Each source sits at 5 doors (see `sku()`); the aggregated cell displays max = 5.
      // Typing 500 must set every source to 500 so the cell redisplays 500, not 250.
      expect(findNode(next, "s1").sales_data[0].metrics.store_count).toBe(500)
      expect(findNode(next, "s2").sales_data[0].metrics.store_count).toBe(500)
    })

    it("applies the same case_cost to every source SKU (no proportional split)", () => {
      const tree = [
        customer("c1", "Berry Jam", [sku("s1", "12oz", 100, 24)]),
        customer("c2", "Berry Jam", [sku("s2", "12oz", 200, 48)]),
      ]
      const next = updateAggregatedSKUData(
        tree as any,
        buildAggregatedSKUId("Berry Jam", "12oz"),
        "2026-01",
        "case_cost",
        40
      )
      expect(findNode(next, "s1").case_cost).toBe(40)
      expect(findNode(next, "s2").case_cost).toBe(40)
    })

    it("returns the hierarchy untouched for an id that is not an aggregated SKU id", () => {
      const tree = [customer("c1", "Berry Jam", [sku("s1", "12oz", 100)])]
      const next = updateAggregatedSKUData(tree as any, "aggregated-berry jam", "2026-01", "volume_dollars", 999)
      expect(next).toBe(tree)
    })
  })

  describe("updateCaseCostInHierarchy", () => {
    it("updates case_cost and opportunity_base_metrics across the matching opportunity rows", () => {
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      const opp = { id: "opp-42", name: "Launch", likelihood_pct: 50, status: "open" as const, color: "#0ea5e9" }
      const tree = [
        {
          id: "root",
          level: HierarchyLevel.CLASS_OF_TRADE,
          children: [
            {
              id: "op-row-a",
              level: HierarchyLevel.PRODUCT_LINE,
              case_cost: 10,
              metadata: { opportunity: opp },
              sales_data: [
                {
                  period,
                  metrics: { store_count: 10, cases_sold: 5, volume_dollars: 250, case_cost: 10 },
                  opportunity_base_metrics: { store_count: 20, cases_sold: 10, volume_dollars: 500, case_cost: 10 },
                },
              ],
            },
            {
              id: "op-row-b",
              level: HierarchyLevel.PRODUCT_LINE,
              case_cost: 10,
              metadata: { opportunity: opp },
              sales_data: [
                {
                  period,
                  metrics: { store_count: 6, cases_sold: 4, volume_dollars: 120, case_cost: 10 },
                  opportunity_base_metrics: { store_count: 12, cases_sold: 8, volume_dollars: 240, case_cost: 10 },
                },
              ],
            },
          ],
        },
      ]
      const out = updateCaseCostInHierarchy(tree as any, "op-row-a", 22)
      const a = (out[0] as any).children[0]
      const b = (out[0] as any).children[1]
      expect(a.case_cost).toBe(22)
      expect(b.case_cost).toBe(22)
      expect(a.sales_data[0].metrics.case_cost).toBe(22)
      expect(b.sales_data[0].metrics.case_cost).toBe(22)
      expect(a.sales_data[0].opportunity_base_metrics.case_cost).toBe(22)
      expect(b.sales_data[0].opportunity_base_metrics.case_cost).toBe(22)
    })
  })

  describe("recalculateVolumeForAllRows", () => {
    it("updates volume for cost-based rows without touching pure-revenue rows", () => {
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      const tree = [
        {
          id: "cost-row",
          level: HierarchyLevel.PRODUCT_LINE,
          case_cost: 20,
          sales_data: [
            { period, metrics: { store_count: 10, cases_sold: 5, volume_dollars: 999, case_cost: 20 }, is_forecast: true },
          ],
          children: [],
        },
        {
          id: "revenue-row",
          level: HierarchyLevel.PRODUCT_LINE,
          case_cost: 0,
          sales_data: [
            { period, metrics: { store_count: 0, cases_sold: 0, volume_dollars: 1234, case_cost: 0 }, is_forecast: true },
          ],
          children: [],
        },
      ]
      const result = recalculateVolumeForAllRows(tree as any)
      const costRow = result.find((n: any) => n.id === "cost-row")
      const revenueRow = result.find((n: any) => n.id === "revenue-row")
      // Cost-based row: volume = 10 * 5 * 20 = 1000
      expect(costRow.sales_data[0].metrics.volume_dollars).toBe(1000)
      // Pure-revenue row: volume unchanged
      expect(revenueRow.sales_data[0].metrics.volume_dollars).toBe(1234)
    })

    it("uses opportunity_base_metrics for volume when metrics are already likelihood-scaled", () => {
      // Simulates the state after updateOpportunityLikelihood(data, oppId, 50) has been called:
      // metrics reflect 50% likelihood, opportunity_base_metrics hold the unscaled truth.
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      const opp = { id: "opp-1", name: "Launch", likelihood_pct: 50, status: "open" as const, color: "#0ea5e9" }
      const tree = [
        {
          id: "opp-row",
          level: HierarchyLevel.PRODUCT_LINE,
          case_cost: 10,
          metadata: { opportunity: opp },
          sales_data: [{
            period,
            // metrics scaled to 50% by updateOpportunityLikelihood
            metrics: { store_count: 50, cases_sold: 100, volume_dollars: 500, case_cost: 10 },
            opportunity_base_metrics: { store_count: 100, cases_sold: 200, volume_dollars: 1000, case_cost: 10 },
          }],
          children: [],
        },
      ]

      // User changes case_cost to 20
      const updated = recalculateVolumeForAllRows(
        [{ ...tree[0], case_cost: 20 }] as any
      )
      const dp = updated[0].sales_data[0]

      // opportunity_base_metrics must use unscaled store/cases (100 × 200 × 20 = 400,000)
      expect(dp.opportunity_base_metrics.store_count).toBe(100)
      expect(dp.opportunity_base_metrics.cases_sold).toBe(200)
      expect(dp.opportunity_base_metrics.volume_dollars).toBe(400_000)
      expect(dp.opportunity_base_metrics.case_cost).toBe(20)

      // metrics store/case counts must remain unchanged (still the likelihood-scaled values)
      expect(dp.metrics.store_count).toBe(50)
      expect(dp.metrics.cases_sold).toBe(100)
      expect(dp.metrics.case_cost).toBe(20)
    })
  })

  describe("aggregateChildrenToParents compare rollup", () => {
    it("rolls forecast_metrics, actual_metrics, and variance to parent", () => {
      const period = { year: 2026, month: 1, period_key: "2026-01" }
      const leafA = {
        period,
        metrics: { store_count: 1, volume_dollars: 100, cases_sold: 10, case_cost: 0 },
        forecast_metrics: { store_count: 1, volume_dollars: 120, cases_sold: 12 },
        actual_metrics: { store_count: 1, volume_dollars: 100, cases_sold: 10 },
      }
      const leafB = {
        period,
        metrics: { store_count: 2, volume_dollars: 200, cases_sold: 20, case_cost: 0 },
        forecast_metrics: { store_count: 2, volume_dollars: 240, cases_sold: 24 },
        actual_metrics: { store_count: 2, volume_dollars: 200, cases_sold: 20 },
      }
      const tree = [
        {
          id: "parent",
          name: "P",
          level: HierarchyLevel.CUSTOMER,
          sales_data: [],
          children: [
            { id: "c1", level: HierarchyLevel.SKU, sales_data: [leafA], children: [] },
            { id: "c2", level: HierarchyLevel.SKU, sales_data: [leafB], children: [] },
          ],
        },
      ]
      const out = aggregateChildrenToParents(tree as any)
      const parentDp = out[0].sales_data.find((d: any) => d.period.period_key === "2026-01")
      expect(parentDp?.variance).toBeDefined()
      expect(parentDp?.forecast_metrics?.volume_dollars).toBe(360)
      expect(parentDp?.actual_metrics?.volume_dollars).toBe(300)
      expect(parentDp?.variance?.volume_dollars_variance).toBe(-60)
      expect(parentDp?.compare_axis).toBe("dollars")
    })
  })

  describe("apply_metric_edit_to_data_point", () => {
    function dp(metrics: Record<string, number | null>, extra: Record<string, unknown> = {}) {
      return { period: { period_key: "2026-01" }, metrics, ...extra }
    }

    it("scales dollars proportionally on a cases edit even with no case_cost (imported rows)", () => {
      const out = apply_metric_edit_to_data_point(dp({ store_count: 0, cases_sold: 50, volume_dollars: 1000, case_cost: 0 }), "cases_sold", 75)
      expect(out.metrics.cases_sold).toBe(75)
      expect(out.metrics.volume_dollars).toBe(1500)
    })

    it("scales dollars proportionally on a store count edit", () => {
      const out = apply_metric_edit_to_data_point(dp({ store_count: 10, cases_sold: 5, volume_dollars: 1000, case_cost: 20 }), "store_count", 20)
      expect(out.metrics.store_count).toBe(20)
      expect(out.metrics.volume_dollars).toBe(2000)
      // cases are per-store in planning rows; a store edit must not change them
      expect(out.metrics.cases_sold).toBe(5)
    })

    it("holds the per-store rate on a store count edit when the row's quantity is the account total", () => {
      // Sanzo, Walmart, Mango 6-pack: 848 units over 1,248 doors at $5.14. Cutting doors has to
      // take units down with the dollars, or the row's price per unit drifts off $5.14.
      const out = apply_metric_edit_to_data_point(
        dp({ store_count: 1248, cases_sold: 848, units_sold: 848, volume_dollars: 4358.72, case_cost: 5.14 }),
        "store_count",
        1200,
      )
      expect(out.metrics.store_count).toBe(1200)
      // Cases keep the fraction (848 × 1200/1248 = 815.38); units stay whole.
      expect(out.metrics.cases_sold).toBe(815.38)
      expect(out.metrics.units_sold).toBe(815)
      expect(out.metrics.volume_dollars).toBeCloseTo(4191.08, 2)
    })

    it("scales dollars and cases on a units edit (the units edit bug)", () => {
      const out = apply_metric_edit_to_data_point(dp({ store_count: 0, cases_sold: 10, units_sold: 60, volume_dollars: 600, case_cost: 0 }), "units_sold", 120)
      expect(out.metrics.units_sold).toBe(120)
      expect(out.metrics.volume_dollars).toBe(1200)
      expect(out.metrics.cases_sold).toBe(20)
    })

    it("zeroing a quantity zeroes the dollars", () => {
      const out = apply_metric_edit_to_data_point(dp({ store_count: 10, cases_sold: 5, volume_dollars: 1000, case_cost: 20 }), "cases_sold", 0)
      expect(out.metrics.volume_dollars).toBe(0)
    })

    it("populates dollars from the node cost when the row has no volume yet", () => {
      const out = apply_metric_edit_to_data_point(dp({ store_count: 10, cases_sold: 0, volume_dollars: 0, case_cost: 0 }), "cases_sold", 5, 20)
      expect(out.metrics.volume_dollars).toBe(10 * 5 * 20)
    })

    it("populates dollars as cases × cost when the row has no store data", () => {
      const out = apply_metric_edit_to_data_point(dp({ store_count: 0, cases_sold: 0, volume_dollars: 0, case_cost: 25 }), "cases_sold", 4)
      expect(out.metrics.volume_dollars).toBe(100)
    })

    it("never rewrites dollars when the old quantity is 0 but revenue exists", () => {
      const out = apply_metric_edit_to_data_point(dp({ store_count: 0, cases_sold: 50, volume_dollars: 1234, case_cost: 20 }), "store_count", 3)
      expect(out.metrics.store_count).toBe(3)
      expect(out.metrics.volume_dollars).toBe(1234)
    })

    it("volume edits set dollars directly and carry the quantities at the row's implied price", () => {
      // Typing over revenue used to leave cases where they were, so stores × cases × cost no
      // longer footed to the cell (1st Phorm, 10/27). Doors stay: revenue is not distribution.
      const out = apply_metric_edit_to_data_point(dp({ store_count: 10, cases_sold: 5, volume_dollars: 1000, case_cost: 20 }), "volume_dollars", 2000)
      expect(out.metrics.volume_dollars).toBe(2000)
      expect(out.metrics.cases_sold).toBe(10)
      expect(out.metrics.store_count).toBe(10)
    })

    it("a volume edit on a row with no dollars yet sets the dollars and leaves the quantities", () => {
      const out = apply_metric_edit_to_data_point(dp({ store_count: 10, cases_sold: 5, volume_dollars: 0, case_cost: 20 }), "volume_dollars", 555)
      expect(out.metrics.volume_dollars).toBe(555)
      expect(out.metrics.cases_sold).toBe(5)
    })

    it("updates the displayed detail-slice values on a cases edit (retailer detail rows)", () => {
      // The grid displays detail values when present; editing only metrics looked like the edit reverted.
      const out = apply_metric_edit_to_data_point(
        dp({
          store_count: 12,
          cases_sold: 100,
          units_sold: 600,
          volume_dollars: 3000,
          case_cost: 0,
          detail: {
            consumption_gross_sales: 3000,
            gross_sales_delivered: 2800,
            forecasted_consumption_cases: 100,
            forecasted_delivery_cases: 95,
            forecasted_consumption_units: 600,
          } as any,
        }),
        "cases_sold",
        200
      )
      expect(out.metrics.detail.forecasted_consumption_cases).toBe(200)
      expect(out.metrics.detail.consumption_gross_sales).toBe(6000)
      expect(out.metrics.detail.gross_sales_delivered).toBe(5600)
      expect(out.metrics.detail.forecasted_delivery_cases).toBe(190)
      expect(out.metrics.detail.forecasted_consumption_units).toBe(1200)
      expect(out.metrics.volume_dollars).toBe(6000)
      expect(out.metrics.cases_sold).toBe(200)
    })

    it("updates the displayed detail-slice values on a units edit", () => {
      const out = apply_metric_edit_to_data_point(
        dp({
          store_count: 0,
          cases_sold: 50,
          units_sold: 300,
          volume_dollars: 1500,
          case_cost: 0,
          detail: {
            consumption_gross_sales: 1500,
            forecasted_consumption_cases: 50,
            forecasted_consumption_units: 300,
          } as any,
        }),
        "units_sold",
        600
      )
      expect(out.metrics.detail.forecasted_consumption_units).toBe(600)
      expect(out.metrics.detail.consumption_gross_sales).toBe(3000)
      expect(out.metrics.detail.forecasted_consumption_cases).toBe(100)
      expect(out.metrics.volume_dollars).toBe(3000)
    })

    it("scales detail dollars on a store count edit (the store-edit bug on detail rows)", () => {
      const out = apply_metric_edit_to_data_point(
        dp({
          store_count: 10,
          cases_sold: 100,
          volume_dollars: 3000,
          case_cost: 0,
          detail: { consumption_gross_sales: 3000, forecasted_consumption_cases: 100 } as any,
        }),
        "store_count",
        20
      )
      expect(out.metrics.store_count).toBe(20)
      expect(out.metrics.detail.consumption_gross_sales).toBe(6000)
      expect(out.metrics.volume_dollars).toBe(6000)
      // cases stay per the planning model
      expect(out.metrics.detail.forecasted_consumption_cases).toBe(100)
    })

    it("volume edit on the Delivered slice only touches delivered dollars", () => {
      const out = apply_metric_edit_to_data_point(
        dp({
          store_count: 0,
          cases_sold: 50,
          volume_dollars: 1500,
          case_cost: 0,
          detail: { consumption_gross_sales: 1500, gross_sales_delivered: 1400 } as any,
        }),
        "volume_dollars",
        2000,
        0,
        { show_stores: true, show_cases: true, show_volume: true, forecast_dollar_basis: "gross_sales_delivered" }
      )
      expect(out.metrics.detail.gross_sales_delivered).toBe(2000)
      expect(out.metrics.detail.consumption_gross_sales).toBe(1500)
      expect(out.metrics.volume_dollars).toBe(1500)
    })

    it("keeps promomash base/incremental/total breakdown in step with a units edit", () => {
      const out = apply_metric_edit_to_data_point(
        dp({
          store_count: 0,
          cases_sold: 0,
          units_sold: 100,
          volume_dollars: 1000,
          case_cost: 0,
          detail: { base: 80, incremental: 20, total: 100 } as any,
        }),
        "units_sold",
        200
      )
      expect(out.metrics.units_sold).toBe(200)
      expect(out.metrics.detail.base).toBe(160)
      expect(out.metrics.detail.incremental).toBe(40)
      expect(out.metrics.detail.total).toBe(200)
      expect(out.metrics.volume_dollars).toBe(2000)
    })

    it("keeps opportunity base metrics in step with the edit", () => {
      const out = apply_metric_edit_to_data_point(
        dp(
          { store_count: 5, cases_sold: 10, volume_dollars: 500, case_cost: 10 },
          { opportunity_base_metrics: { store_count: 10, cases_sold: 20, volume_dollars: 1000, case_cost: 10 } }
        ),
        "cases_sold",
        20
      )
      expect(out.metrics.volume_dollars).toBe(1000)
      expect(out.opportunity_base_metrics.cases_sold).toBe(20)
      expect(out.opportunity_base_metrics.volume_dollars).toBe(1000)
    })
  })

  describe("updateDataInHierarchy with shadowed rows (promomash forecast + volume_based_estimate)", () => {
    it("edits only the primary forecast row and leaves the estimate row untouched", () => {
      const nodes = [
        {
          id: "leaf",
          level: HierarchyLevel.SKU,
          name: "Sku",
          sales_data: [
            {
              period: { period_key: "2026-W01" },
              source_kind: "forecast",
              metrics: { store_count: 0, cases_sold: 0, units_sold: 100, volume_dollars: 0, case_cost: 0 },
            },
            {
              period: { period_key: "2026-W01" },
              source_kind: "volume_based_estimate",
              is_locked: true,
              metrics: { store_count: 0, cases_sold: 0, units_sold: 90, volume_dollars: 0, case_cost: 0 },
            },
          ],
          children: [],
        },
      ]
      const out = updateDataInHierarchy(nodes, "leaf", "2026-W01", "units_sold", 200)
      const forecast_row = out[0].sales_data.find((d: any) => d.source_kind === "forecast")
      const vbe_row = out[0].sales_data.find((d: any) => d.source_kind === "volume_based_estimate")
      expect(forecast_row.metrics.units_sold).toBe(200)
      expect(vbe_row.metrics.units_sold).toBe(90)
    })
  })

  describe("aggregateChildrenToParents with shadowed rows", () => {
    it("rolls up only the primary forecast row per period (no double count)", () => {
      const tree = [
        {
          id: "parent",
          name: "P",
          level: HierarchyLevel.CUSTOMER,
          sales_data: [],
          children: [
            {
              id: "leaf",
              level: HierarchyLevel.SKU,
              name: "Sku",
              sales_data: [
                {
                  period: { period_key: "2026-W01" },
                  source_kind: "forecast",
                  metrics: { store_count: 0, cases_sold: 0, units_sold: 100, volume_dollars: 0, case_cost: 0 },
                },
                {
                  period: { period_key: "2026-W01" },
                  source_kind: "volume_based_estimate",
                  metrics: { store_count: 0, cases_sold: 0, units_sold: 90, volume_dollars: 0, case_cost: 0 },
                },
              ],
              children: [],
            },
          ],
        },
      ]
      const out = aggregateChildrenToParents(tree as any)
      const parent_dp = out[0].sales_data.find((d: any) => d.period.period_key === "2026-W01")
      expect(parent_dp.metrics.units_sold).toBe(100)
    })
  })

  describe("updateDataInHierarchy", () => {
    it("updates dollars for the edited cell only", () => {
      const nodes = [
        {
          id: "leaf",
          level: HierarchyLevel.SKU,
          name: "Sku",
          sales_data: [
            { period: { period_key: "2026-01" }, metrics: { store_count: 0, cases_sold: 10, volume_dollars: 200, case_cost: 0 } },
            { period: { period_key: "2026-02" }, metrics: { store_count: 0, cases_sold: 10, volume_dollars: 300, case_cost: 0 } },
          ],
          children: [],
        },
      ]
      const out = updateDataInHierarchy(nodes, "leaf", "2026-01", "cases_sold", 20)
      expect(out[0].sales_data[0].metrics.volume_dollars).toBe(400)
      expect(out[0].sales_data[1].metrics.volume_dollars).toBe(300)
    })
  })

  describe("reprice_data_point_for_case_cost", () => {
    it("scales plan-row dollars by the cost ratio", () => {
      const out = reprice_data_point_for_case_cost(
        { source_kind: "forecast", metrics: { store_count: 10, cases_sold: 5, volume_dollars: 1000, case_cost: 20 } },
        30
      )
      expect(out.metrics.case_cost).toBe(30)
      expect(out.metrics.volume_dollars).toBe(1500)
    })

    it("computes dollars from stores × cases × cost when there is no baseline", () => {
      const out = reprice_data_point_for_case_cost(
        { source_kind: "forecast", metrics: { store_count: 10, cases_sold: 5, volume_dollars: 0, case_cost: 0 } },
        20
      )
      expect(out.metrics.volume_dollars).toBe(1000)
    })

    it("leaves actual and locked rows' dollars untouched", () => {
      const actual = reprice_data_point_for_case_cost(
        { source_kind: "actual", metrics: { store_count: 0, cases_sold: 48, volume_dollars: 960, case_cost: 20 } },
        99
      )
      expect(actual.metrics.volume_dollars).toBe(960)
      expect(actual.metrics.case_cost).toBe(99)
      const locked = reprice_data_point_for_case_cost(
        { source_kind: "forecast", is_locked: true, metrics: { cases_sold: 10, volume_dollars: 500, case_cost: 50 } },
        99
      )
      expect(locked.metrics.volume_dollars).toBe(500)
    })
  })

  describe("recalculateVolumeForAllRows exemptions", () => {
    it("does not rewrite actual, locked, or imported row dollars", () => {
      const nodes = [
        {
          id: "leaf",
          level: HierarchyLevel.SKU,
          name: "Sku",
          case_cost: 20,
          sales_data: [
            { period: { period_key: "2026-01" }, source_kind: "actual", metrics: { store_count: 0, cases_sold: 48, volume_dollars: 960, case_cost: 20 } },
            { period: { period_key: "2026-02" }, source_kind: "imported", metrics: { store_count: 0, cases_sold: 10, volume_dollars: 123, case_cost: 0 } },
            { period: { period_key: "2026-03" }, source_kind: "forecast", is_locked: true, metrics: { store_count: 2, cases_sold: 10, volume_dollars: 777, case_cost: 20 } },
            { period: { period_key: "2026-04" }, source_kind: "forecast", metrics: { store_count: 2, cases_sold: 10, volume_dollars: 0, case_cost: 20 } },
          ],
          children: [],
        },
      ]
      const out = recalculateVolumeForAllRows(nodes)
      const [actual, imported, locked, plan] = out[0].sales_data
      expect(actual.metrics.volume_dollars).toBe(960)
      expect(imported.metrics.volume_dollars).toBe(123)
      expect(locked.metrics.volume_dollars).toBe(777)
      expect(plan.metrics.volume_dollars).toBe(2 * 10 * 20)
    })
  })

  describe("updateForecastConfigInHierarchy", () => {
    it("merges multipliers into the existing config instead of replacing it", () => {
      const nodes = [
        {
          id: "n1",
          level: HierarchyLevel.SKU,
          name: "Sku",
          forecast_config: { initial_doors: 10, initial_cases_per_month: 5, cost_per_case: 20, same_store_sales_multiplier: 0.03, store_count_growth_multiplier: 0.02 },
          sales_data: [],
          children: [],
        },
      ]
      const out = updateForecastConfigInHierarchy(nodes, "n1", { same_store_sales_multiplier: 0.05, store_count_growth_multiplier: 0.01 })
      expect(out[0].forecast_config.same_store_sales_multiplier).toBe(0.05)
      expect(out[0].forecast_config.cost_per_case).toBe(20)
      expect(out[0].forecast_config.initial_doors).toBe(10)
    })
  })
  describe("part-cases roll up to a parent", () => {
    function leaf(cases_sold: number, volume_dollars: number): any {
      return { period: { period_key: "2027-W05" }, metrics: { store_count: 10, cases_sold, volume_dollars, case_cost: 20.56 } }
    }

    it("sums the part-cases of zero-case children, so a parent of quarter cases is not a zero", () => {
      // Three SKUs of one 6-pack each: a quarter tray apiece, stored as 0 cases.
      const two = rollupMergeSalesDataPoints(leaf(0, 5.14), leaf(0, 5.14))
      const three = rollupMergeSalesDataPoints(two, leaf(0, 5.14))
      expect(three.metrics.cases_sold).toBe(0)
      expect(three.metrics.part_cases).toBe(0.75)
    })

    it("counts only the children that hold less than a whole case", () => {
      const parent = rollupMergeSalesDataPoints(leaf(279, 5736.24), leaf(0, 10.28))
      expect(parent.metrics.cases_sold).toBe(279)
      expect(parent.metrics.part_cases).toBe(0.5)
    })
  })

  describe("spread_parent_edit", () => {
    function sum(values: Map<string, number>): number {
      return Math.round([...values.values()].reduce((s: number, v: number) => s + v, 0) * 100) / 100
    }

    it("scales every leaf by the same ratio", () => {
      const out = spread_parent_edit(
        [{ id: "a", value: 600 }, { id: "b", value: 300 }, { id: "c", value: 100 }],
        1100,
      )
      expect(out.get("a")).toBe(660)
      expect(out.get("b")).toBe(330)
      expect(out.get("c")).toBe(110)
    })

    it("splits a total evenly when nothing underneath has a value yet", () => {
      const out = spread_parent_edit([{ id: "a", value: 0 }, { id: "b", value: 0 }], 99)
      // 49.50 each: an even split, exact to the cent.
      expect(out.get("a")).toBe(49.5)
      expect(out.get("b")).toBe(49.5)
    })

    it("splits a zero-revenue total by the case counts when those exist", () => {
      const out = spread_parent_edit(
        [{ id: "a", value: 0, weight: 30 }, { id: "b", value: 0, weight: 10 }],
        1000,
      )
      expect(out.get("a")).toBe(750)
      expect(out.get("b")).toBe(250)
    })

    it("foots to the typed total to the cent, remainder on the largest leaf", () => {
      const leaves = [{ id: "a", value: 1 }, { id: "b", value: 1 }, { id: "c", value: 1 }]
      const out = spread_parent_edit(leaves, 100)
      expect(sum(out)).toBe(100)
      // 33.33 + 33.33 + 33.34 — the odd cent lands on one leaf, and all are "largest" here so the first takes it.
      expect([...out.values()].sort()).toEqual([33.33, 33.33, 33.34])
      const uneven = spread_parent_edit([{ id: "small", value: 1 }, { id: "big", value: 2 }], 10)
      expect(sum(uneven)).toBe(10)
      expect(uneven.get("big")).toBe(6.67)
      expect(uneven.get("small")).toBe(3.33)
    })

    it("skips locked leaves; the rest absorb the whole change", () => {
      const out = spread_parent_edit(
        [{ id: "locked", value: 400, is_locked: true }, { id: "a", value: 400 }, { id: "b", value: 200 }],
        1300,
      )
      expect(out.has("locked")).toBe(false)
      // 1300 − 400 locked = 900 over a 2:1 split.
      expect(out.get("a")).toBe(600)
      expect(out.get("b")).toBe(300)
    })

    it("works in whole units when asked", () => {
      const out = spread_parent_edit([{ id: "a", value: 10 }, { id: "b", value: 10 }], 25, 0)
      expect(sum(out)).toBe(25)
      expect([...out.values()].sort()).toEqual([12, 13])
    })

    it("hands back nothing when every leaf is locked", () => {
      expect(spread_parent_edit([{ id: "a", value: 1, is_locked: true }], 50).size).toBe(0)
    })
  })

  describe("spread_parent_edit_in_hierarchy (through updateDataInHierarchy)", () => {
    function sku(id: string, cases: number, dollars: number, extra: Record<string, unknown> = {}, metadata: Record<string, unknown> | null = null) {
      return {
        id,
        name: id,
        level: HierarchyLevel.SKU,
        metadata,
        children: [],
        sales_data: [
          {
            period: { period_key: "2027-03", year: 2027, month: 3 },
            metrics: { store_count: 20, volume_dollars: dollars, cases_sold: cases, case_cost: dollars / cases },
            is_forecast: true,
            source_kind: "forecast",
            ...extra,
          },
        ],
      }
    }

    function customer(children: any[]) {
      return aggregateChildrenToParents([
        { id: "kroger", name: "Kroger", level: HierarchyLevel.CUSTOMER, children: [
          { id: "pl", name: "Sparkling", level: HierarchyLevel.PRODUCT_LINE, children, sales_data: [] },
        ], sales_data: [] },
      ])
    }

    function leaves(roots: any[]): any[] {
      return roots[0].children[0].children
    }

    it("spreads a customer revenue edit over the SKUs by their share, cases following", () => {
      const roots = customer([sku("a", 100, 1000), sku("b", 50, 1000)])
      const next = aggregateChildrenToParents(updateDataInHierarchy(roots, "kroger", "2027-03", "volume_dollars", 3000))
      const [a, b] = leaves(next).map((n: any) => n.sales_data[0].metrics)
      expect(a.volume_dollars).toBe(1500)
      expect(b.volume_dollars).toBe(1500)
      expect(a.cases_sold).toBe(150)
      expect(b.cases_sold).toBe(75)
      // Store counts never move on a parent edit.
      expect(a.store_count).toBe(20)
      expect(next[0].sales_data[0].metrics.volume_dollars).toBe(3000)
    })

    it("holds actual and locked leaves still", () => {
      const roots = customer([
        sku("actual", 10, 100, { source_kind: "actual", is_actual: true, is_forecast: false }),
        sku("locked", 10, 100, { is_locked: true }),
        sku("open", 10, 100),
      ])
      const next = updateDataInHierarchy(roots, "kroger", "2027-03", "volume_dollars", 500)
      const [actual, locked, open] = leaves(next).map((n: any) => n.sales_data[0].metrics)
      expect(actual.volume_dollars).toBe(100)
      expect(locked.volume_dollars).toBe(100)
      expect(open.volume_dollars).toBe(300)
    })

    it("spreads a cases edit from a product line and reprices each SKU at its own cost", () => {
      const roots = customer([sku("a", 100, 1000), sku("b", 100, 2000)])
      const next = updateDataInHierarchy(roots, "pl", "2027-03", "cases_sold", 300)
      const [a, b] = leaves(next).map((n: any) => n.sales_data[0].metrics)
      expect(a.cases_sold).toBe(150)
      expect(b.cases_sold).toBe(150)
      expect(a.volume_dollars).toBe(1500)
      expect(b.volume_dollars).toBe(3000)
    })

    it("takes a units edit through each SKU's pack size and leaves a SKU with none alone", () => {
      const roots = customer([
        sku("twelve", 10, 120, {}, { units_per_case: 12 }),
        sku("six", 10, 120, {}, { units_per_case: 6 }),
        sku("unknown", 10, 120),
      ])
      // 120 + 60 units today; 360 doubles both packs.
      const next = updateDataInHierarchy(roots, "kroger", "2027-03", "units_sold", 360)
      const [twelve, six, unknown] = leaves(next).map((n: any) => n.sales_data[0].metrics)
      expect(twelve.cases_sold).toBe(20)
      expect(twelve.volume_dollars).toBe(240)
      expect(six.cases_sold).toBe(20)
      expect(unknown.cases_sold).toBe(10)
      expect(unknown.volume_dollars).toBe(120)
    })

    it("distributes evenly under a parent whose leaves are all at zero", () => {
      const roots = customer([
        { ...sku("a", 0, 0), case_cost: 10 },
        { ...sku("b", 0, 0), case_cost: 10 },
      ])
      const next = updateDataInHierarchy(roots, "kroger", "2027-03", "volume_dollars", 1000)
      const [a, b] = leaves(next).map((n: any) => n.sales_data[0].metrics)
      expect(a.volume_dollars).toBe(500)
      expect(b.volume_dollars).toBe(500)
    })

    it("still edits a leaf row directly", () => {
      const roots = customer([sku("a", 100, 1000)])
      const next = updateDataInHierarchy(roots, "a", "2027-03", "volume_dollars", 2000)
      expect(leaves(next)[0].sales_data[0].metrics.volume_dollars).toBe(2000)
    })
  })
}
